import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Smartphone, Scale, Leaf, Clock, 
  Dumbbell, Lock, Unlock, 
  Trash2, ChefHat, MessageCircle,
  Plus, Save, Edit2, ChevronDown, CheckCircle, 
  FileText, Activity, Flame, X, ClipboardList, TrendingDown, TrendingUp, Minus, Heart, VenusAndMars,
  UserCircle, Languages, Calendar, Cake, Mail, Phone, MapPin, Globe, Map, Ruler, Target,
  AlertTriangle, Ban, NotebookPen
} from 'lucide-react';
import { useClient } from '../context/ClientContext';
import { useTheme } from '../context/ThemeContext';
import ClientWorkspaceLayout from '../components/workspace/ClientWorkspaceLayout';
import ClientSnapshotBar from '../components/client/ClientSnapshotBar';
import PageHeader from '../components/common/PageHeader';
import { authClient } from '../lib/authClient';
import {
  fetchClientById,
  fetchClientDailyTargets,
  fetchClientMealPlanStructure,
  fetchClientRecommendations,
  fetchClientConversations,
  fetchConversationMessages,
  updateClientSettings,
  fetchFoodLogs,
} from '../services/clientApi';
import { isDemoUserCode, getDemoMealPlans, getDemoWeightLogs } from '../services/tourDemo';
import {
  goalOptions,
  activityLevelOptions,
  countryCodes,
  timezones,
  allergiesOptions,
  limitationsOptions,
  regions,
  aiLanguages,
} from '../constants/profileOptions';
import { calculateBMR, getActivityMultiplier, getGoalFactor, calculateBMI } from '../utils/nutritionMath';
import { toStructureArray } from '../utils/mealPlanTargets';
import API_BASE_URL from '../config/api';

// Premium Accordion Section
function ProfileSection({ title, sectionKey, icon, children, expandedSection, setExpandedSection, isDarkMode, textMutedClass, tourAnchor }) {
  const isExpanded = expandedSection === sectionKey;
  return (
    <div
      data-tour={tourAnchor ? `${tourAnchor}-expanded` : undefined}
      className={`rounded-lg border transition-all duration-300 ${isDarkMode ? 'bg-surface/40 border-line' : 'bg-surface border-line'} ${isExpanded ? 'mb-4 last:mb-0 ' : 'mb-3 last:mb-0'}`}
    >
      <button
        type="button"
        data-tour={tourAnchor ? `${tourAnchor}-toggle` : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setExpandedSection(isExpanded ? 'none' : sectionKey);
        }}
        className="w-full flex justify-between items-center py-4 px-5 text-left focus:outline-none rounded-lg hover:bg-canvas dark:hover:bg-surface/50 transition-colors"
      >
        <span className="flex items-center gap-3">
          {icon && (
            <span className={`flex items-center justify-center w-8 h-8 rounded-lg ${isDarkMode ? 'bg-moss text-ink-muted' : 'bg-moss text-ink-muted'} `}>
              {icon}
            </span>
          )}
          <span className={`text-sm font-bold uppercase tracking-wider ${isDarkMode ? 'text-ink' : 'text-ink'}`}>{title}</span>
        </span>
        <span
          data-tour={tourAnchor || undefined}
          className={`${textMutedClass} inline-flex items-center justify-center w-9 h-9 rounded-lg transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
        >
          <ChevronDown size={20} />
        </span>
      </button>
      <div className={`grid transition-all duration-300 ${isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <div className="px-5 pb-5 pt-1">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function resolveCustomAnswerKeys(stepId) {
  const id = String(stepId || '').trim();
  if (!id) return [];
  const keys = [id];
  if (id.startsWith('custom_')) {
    keys.push(id.slice(7).replace(/_/g, '-'));
  } else {
    keys.push(`custom_${id.replace(/-/g, '_')}`);
  }
  return [...new Set(keys)];
}

function lookupCustomAnswer(answersObj, stepId) {
  for (const key of resolveCustomAnswerKeys(stepId)) {
    if (!Object.prototype.hasOwnProperty.call(answersObj, key)) continue;
    const val = answersObj[key];
    if (val == null || val === '') continue;
    if (Array.isArray(val) && val.length === 0) continue;
    return val;
  }
  return undefined;
}

function formatCustomOnboardingAnswer(step, rawAnswer) {
  if (rawAnswer == null || rawAnswer === '') return { display: null, values: [] };
  if (step?.type === 'multiselect') {
    const arr = Array.isArray(rawAnswer) ? rawAnswer : [rawAnswer];
    const values = arr.map(String).map((s) => s.trim()).filter(Boolean);
    return { display: values.join(', ') || null, values };
  }
  const text = String(rawAnswer).trim();
  return { display: text || null, values: text ? [text] : [] };
}

function buildCustomOnboardingItems(steps, answers) {
  const answersObj = answers && typeof answers === 'object' && !Array.isArray(answers) ? answers : {};
  const normalizedSteps = (Array.isArray(steps) ? steps : []).map((step) => ({
    ...step,
    options: Array.isArray(step.options)
      ? step.options
      : typeof step.options === 'string'
        ? step.options.split(/[,;\n]/).map((o) => o.trim()).filter(Boolean)
        : [],
  }));
  return normalizedSteps.map((step) => {
    const rawAnswer = lookupCustomAnswer(answersObj, step.id);
    const { display, values } = formatCustomOnboardingAnswer(step, rawAnswer);
    return {
      ...step,
      answer: display,
      answerValues: values,
    };
  });
}

function DashboardPage() {
  const navigate = useNavigate();
  const { selectedClient } = useClient();

  // Opening a client (roster row, switcher) always starts at the top, not at the roster's scroll offset.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [selectedClient?.id]);

  const { isDarkMode, themeClasses: baseThemeClasses } = useTheme();

  // Premium Design Tokens
  const themeClasses = {
    ...baseThemeClasses,
    textGreen: isDarkMode ? 'text-forest-ondark' : 'text-forest-ondark',
    textPurple: isDarkMode ? 'text-purple-400' : 'text-purple-600',
    textRed: isDarkMode ? 'text-red-400' : 'text-red-600',
    inputField: `w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-all duration-200 focus:border-forest/40 focus:ring-2 focus:ring-forest/20 ${isDarkMode ? 'bg-canvas/50 border-line text-ink placeholder-ink-muted' : 'bg-canvas border-line text-ink placeholder-ink-muted'}`,
    select: `w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-all duration-200 focus:border-forest/40 focus:ring-2 focus:ring-forest/20 appearance-none bg-no-repeat pr-10 ${isDarkMode ? 'bg-canvas/50 border-line text-ink [&_option]:bg-surface' : 'bg-canvas border-line text-ink'}`,
    btnPrimaryPill: `px-5 py-2.5 rounded-lg font-medium bg-forest-ondark text-white dark:text-canvas-dark hover:bg-forest-ondark/90   active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas flex items-center justify-center gap-2 disabled:opacity-50 transition-all duration-200`,
    btnSecondaryPill: `px-5 py-2.5 rounded-lg font-medium border border-line dark:border-line bg-surface dark:bg-surface text-ink dark:text-ink hover:bg-canvas dark:hover:bg-moss/50  focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 transition-all duration-200`,
    btnMutedPill: `px-4 py-2.5 rounded-lg font-medium text-ink-muted dark:text-ink-muted hover:bg-moss dark:hover:bg-surface/50 hover:text-ink dark:hover:text-ink focus:outline-none focus:ring-2 focus:ring-forest focus:ring-offset-2 dark:focus:ring-offset-canvas active:scale-[0.98] flex items-center justify-center gap-2 transition-all duration-200`,
    sectionCard: `rounded-lg border p-5 ${isDarkMode ? 'bg-surface/40 border-line' : 'bg-surface border-line'} `,
  };

  const cardBase = `${themeClasses.bgCard}  rounded-lg border border-line dark:border-line   transition-shadow duration-300`;
  const detailTile = `rounded-lg border px-3.5 py-3 flex flex-col gap-1.5 min-w-0 ${
    isDarkMode
      ? 'bg-canvas/40 border-line'
      : 'bg-canvas/90 border-line'
  }`;
  const detailLabelClass = `${themeClasses.textMuted} text-[10px] font-semibold uppercase tracking-wider inline-flex items-center gap-1.5`;
  const detailValueClass = `text-sm font-semibold ${themeClasses.textPrimary} break-words`;
  const detailGridClass = 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3';

  // --- MEAL PLAN STRUCTURE OPTIONS (same as ClientMealPlansPage; 1 of each only) ---
  const MEAL_OPTION_KEYS = [
    'meal.preMorning', 'meal.breakfast', 'meal.morningSnack', 'meal.lunch', 'meal.afternoonSnack',
    'meal.preWorkout', 'meal.postWorkout', 'meal.dinner', 'meal.eveningSnack', 'meal.nightSnack',
  ];
  const mealTranslations = {
    english: {
      'meal.preMorning': 'Pre-Morning Snack', 'meal.breakfast': 'Breakfast', 'meal.morningSnack': 'Morning Snack',
      'meal.lunch': 'Lunch', 'meal.afternoonSnack': 'Afternoon Snack', 'meal.preWorkout': 'Pre-Workout Meal',
      'meal.postWorkout': 'Post-Workout Meal', 'meal.dinner': 'Dinner', 'meal.eveningSnack': 'Evening Snack', 'meal.nightSnack': 'Night Snack',
    },
    hebrew: {
      'meal.preMorning': 'נשנוש בוקר מוקדם', 'meal.breakfast': 'ארוחת בוקר', 'meal.morningSnack': 'ביניים בוקר',
      'meal.lunch': 'ארוחת צהריים', 'meal.afternoonSnack': 'ביניים אחר הצהריים', 'meal.preWorkout': 'לפני אימון',
      'meal.postWorkout': 'אחרי אימון', 'meal.dinner': 'ארוחת ערב', 'meal.eveningSnack': 'ביניים ערב', 'meal.nightSnack': 'ארוחת לילה',
    },
  };
  const getMealLabel = (nameKey) => (mealTranslations[language] || mealTranslations.english)[nameKey] || nameKey;

  // --- LOCAL LANGUAGE STATE ---
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    document.documentElement.dir = direction;
    document.documentElement.lang = language === 'hebrew' ? 'he' : 'en';
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, [direction, language]);

  // --- DATA STATE ---
  const [clientData, setClientData] = useState(null);
  const [dailyTargets, setDailyTargets] = useState(null);
  const [mealPlanStructure, setMealPlanStructure] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [weightLogs, setWeightLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- EDIT MODES ---
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState({});
  const [expandedSection, setExpandedSection] = useState('none');

  useEffect(() => {
    const openPersonalProfile = () => setExpandedSection('personal');
    window.addEventListener('portfolio-playbook-expand-profile', openPersonalProfile);
    return () => window.removeEventListener('portfolio-playbook-expand-profile', openPersonalProfile);
  }, []);

  const [isEditingPreferences, setIsEditingPreferences] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [openMealSelectIndex, setOpenMealSelectIndex] = useState(null);
  const [customAllergyInput, setCustomAllergyInput] = useState('');
  const [customLimitationInput, setCustomLimitationInput] = useState('');

  // --- PREFERENCES FORM STATES ---
  const CALORIES_PER_GRAM = { protein: 4, carbs: 4, fats: 9 };
  const [targetCalories, setTargetCalories] = useState(2000);
  const [macros, setMacros] = useState({
    protein: { percent: 30, grams: 150, locked: false },
    carbs: { percent: 40, grams: 200, locked: false },
    fats: { percent: 30, grams: 67, locked: false },
  });
  const [mealsList, setMealsList] = useState([]);

  const [stats, setStats] = useState({
    totalMenus: 0,
    activeMenus: 0,
    chatSessions: 0,
    weightChange: 0,
  });

  const [currentWeightInput, setCurrentWeightInput] = useState('');

  useEffect(() => {
    const loadClientData = async () => {
      if (!selectedClient?.id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const [
          clientResponse,
          targetsResponse,
          structureResponse,
          conversationsData,
          mealPlansResponse,
        ] = await Promise.allSettled([
          fetchClientById(selectedClient.id),
          fetchClientDailyTargets(selectedClient.id),
          fetchClientMealPlanStructure(selectedClient.id),
          fetchClientConversations(selectedClient.id),
          isDemoUserCode(selectedClient.user_code)
            ? Promise.resolve(getDemoMealPlans())
            : selectedClient.user_code
              ? fetch(`${API_BASE_URL}/meal-plans/${selectedClient.user_code}`).then(r => r.ok ? r.json() : [])
              : Promise.resolve([]),
        ]);

        if (clientResponse.status === 'fulfilled') {
          const data = clientResponse.value;
          setClientData(data);
          setProfileFormData({
            full_name: data.full_name || data.name || '',
            preferred_name: data.preferred_name || '',
            email: data.email || '',
            phone_number: data.phone_number || '',
            whatsapp_number: data.whatsapp_number || '',
            language: data.language || 'en',
            city: data.city || '',
            timezone: data.timezone || '',
            date_of_birth: data.date_of_birth || '',
            age: data.age || '',
            gender: data.gender || '',
            weight: data.weight_kg || data.weight || '',
            height: data.height_cm || data.height || '',
            activity_level: data.Activity_level || data.activity_level || 'sedentary',
            goal: data.goal || 'maintain',
            base_daily_total_calories: data.base_daily_total_calories || '',
            daily_target_total_calories: data.daily_target_total_calories || '',
            food_allergies: Array.isArray(data.food_allergies)
              ? data.food_allergies.map(String).map((s) => s.trim()).filter(Boolean)
              : (typeof data.food_allergies === 'string' && data.food_allergies
                ? data.food_allergies.split(',').map((s) => s.trim()).filter(Boolean)
                : []),
            food_limitations: Array.isArray(data.food_limitations) ? data.food_limitations : [],
            recommendations: data.recommendations || '',
            region: data.region || 'other',
            first_meal_time: data.first_meal_time || '',
            last_meal_time: data.last_meal_time || ''
          });
        }

        let loadedCals = 2000;
        if (targetsResponse.status === 'fulfilled') {
          setDailyTargets(targetsResponse.value);
          loadedCals = parseInt(targetsResponse.value?.calories) || (clientResponse.value?.daily_target_total_calories) || 2000;
          setTargetCalories(loadedCals);

          const pGrams = parseInt(targetsResponse.value?.protein) || Math.round((loadedCals * 0.3) / 4);
          const cGrams = parseInt(targetsResponse.value?.carbs) || Math.round((loadedCals * 0.4) / 4);
          const fGrams = parseInt(targetsResponse.value?.fats) || Math.round((loadedCals * 0.3) / 9);

          setMacros({
            protein: { percent: parseFloat(((pGrams * 4 / loadedCals) * 100).toFixed(1)), grams: pGrams, locked: false },
            carbs: { percent: parseFloat(((cGrams * 4 / loadedCals) * 100).toFixed(1)), grams: cGrams, locked: false },
            fats: { percent: parseFloat(((fGrams * 9 / loadedCals) * 100).toFixed(1)), grams: fGrams, locked: false },
          });
        }

        // Saved target wins. Only estimate from the profile when the client has no saved calorie target.
        const hasSavedCalories = targetsResponse.status === 'fulfilled' && parseInt(targetsResponse.value?.calories, 10) > 0;
        if (clientResponse.status === 'fulfilled' && !hasSavedCalories) {
          const data = clientResponse.value;
          const age = data.age ?? (data.date_of_birth ? (() => { const b = new Date(data.date_of_birth); const t = new Date(); let a = t.getFullYear() - b.getFullYear(); if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--; return a; })() : null);
          const bmr = calculateBMR(age, data.gender, data.weight_kg ?? data.weight, data.height_cm ?? data.height);
          if (bmr != null && !isNaN(bmr)) {
            const activity = data.Activity_level || data.activity_level || 'sedentary';
            const goal = data.goal || 'maintain';
            const tdee = bmr * getActivityMultiplier(activity);
            const suggestedCals = Math.round(tdee * getGoalFactor(goal));
            setTargetCalories(suggestedCals);
            setMacros(prev => {
              const next = { ...prev };
              ['protein', 'carbs', 'fats'].forEach((k) => {
                next[k] = { ...prev[k], grams: Math.round((suggestedCals * (prev[k].percent / 100)) / CALORIES_PER_GRAM[k]) };
              });
              return next;
            });
          }
        }

        if (structureResponse.status === 'fulfilled') {
          const structData = structureResponse.value;
          setMealPlanStructure(structData);
          const normalizeMealKey = (val) => {
            const v = (val || '').trim();
            if (MEAL_OPTION_KEYS.includes(v)) return v;
            const eng = mealTranslations.english;
            const found = Object.keys(eng).find((k) => eng[k] === v);
            return found || 'meal.breakfast';
          };
          const savedSlots = toStructureArray(structData?.structure);
          if (savedSlots.length > 0) {
            const usedKeys = new Set();
            setMealsList(savedSlots.map((meal, idx) => {
              // Support both the new structure (mealSlot/mealName/targetCalories/targetMacros)
              // and the legacy structure (meal/description/calories/calories_pct).
              const slotName = meal.mealSlot ?? meal.meal;
              const description = meal.mealName ?? meal.description ?? '';
              const rawCalories = meal.targetCalories ?? meal.calories;
              let nameKey = normalizeMealKey(slotName || meal.nameKey);
              if (usedKeys.has(nameKey)) nameKey = MEAL_OPTION_KEYS.find((k) => !usedKeys.has(k)) || nameKey;
              usedKeys.add(nameKey);

              const calories = rawCalories !== undefined && rawCalories !== null
                ? rawCalories
                : (meal.calories_pct ? Math.round((meal.calories_pct / 100) * loadedCals) : 0);

              // Preserve per-meal macros set elsewhere (e.g. CreateMealPlanPage). Fall back
              // to a proportional split of the daily macros if none are saved.
              const tm = meal.targetMacros;
              const targetsVal = targetsResponse.status === 'fulfilled' ? (targetsResponse.value || {}) : {};
              const parseG = (v) => parseInt((v ?? '').toString().replace(/[^\d.]/g, '')) || 0;
              const dailyProtein = parseG(targetsVal.protein);
              const dailyCarbs   = parseG(targetsVal.carbs);
              const dailyFats    = parseG(targetsVal.fats || targetsVal.fat);
              const ratio = loadedCals > 0 ? calories / loadedCals : 0;
              const macrosForMeal = (tm && (tm.protein != null || tm.carbs != null || tm.fats != null))
                ? {
                    protein: Math.max(0, Math.round(tm.protein || 0)),
                    carbs:   Math.max(0, Math.round(tm.carbs   || 0)),
                    fats:    Math.max(0, Math.round(tm.fats    || 0)),
                  }
                : {
                    protein: Math.max(0, Math.round(dailyProtein * ratio)),
                    carbs:   Math.max(0, Math.round(dailyCarbs   * ratio)),
                    fats:    Math.max(0, Math.round(dailyFats    * ratio)),
                  };

              return {
                id: meal.id || idx + 1,
                nameKey,
                description,
                calories,
                locked: meal.locked || false,
                macros: macrosForMeal,
              };
            }));
          } else {
            const defaultMealCals = Math.round(loadedCals / 3);
            setMealsList([
              { id: 1, nameKey: 'meal.breakfast', description: '', calories: defaultMealCals, locked: false },
              { id: 2, nameKey: 'meal.lunch', description: '', calories: defaultMealCals, locked: false },
              { id: 3, nameKey: 'meal.dinner', description: '', calories: loadedCals - (defaultMealCals * 2), locked: false },
            ]);
          }
        }

        if (conversationsData.status === 'fulfilled') {
          const convs = conversationsData.value || [];
          setConversations(convs);
          setStats(prev => ({ ...prev, chatSessions: convs.length }));
          if (convs.length > 0) {
            try {
              const messages = await fetchConversationMessages(convs[0].id);
              setRecentMessages((messages || []).slice(-3).reverse());
            } catch (err) {}
          }
        }
        if (mealPlansResponse.status === 'fulfilled') {
          const plans = mealPlansResponse.value || [];
          setMealPlans(Array.isArray(plans) ? plans : []);
          setStats(prev => ({
            ...prev,
            totalMenus: Array.isArray(plans) ? plans.length : 0,
            activeMenus: Array.isArray(plans) ? plans.filter(p => p.isActive || p.status === 'active').length : 0,
          }));
        }

        try {
          const userCode = selectedClient.user_code;
          if (userCode) {
            const weightData = isDemoUserCode(userCode)
              ? { data: getDemoWeightLogs() }
              : await fetch(`${API_BASE_URL}/weight-logs?userCode=${encodeURIComponent(userCode)}`)
                .then((r) => (r.ok ? r.json() : null));
            if (weightData) {
              // Newest first: logs[0] is treated as the current weight below.
              const logs = [...(weightData.data || [])]
                .sort((a, b) => new Date(b.measurement_date) - new Date(a.measurement_date));
              setWeightLogs(logs);
              if (logs.length > 0) {
                setCurrentWeightInput(logs[0].weight_kg != null ? String(logs[0].weight_kg) : '');
              } else {
                const fallback = clientResponse.value?.weight_kg ?? clientResponse.value?.weight;
                setCurrentWeightInput(fallback != null && fallback !== '' ? String(fallback) : '');
              }
              if (logs.length >= 2) {
                const sorted = [...logs].sort((a, b) => new Date(a.measurement_date) - new Date(b.measurement_date));
                const change = sorted[sorted.length - 1].weight_kg - sorted[0].weight_kg;
                setStats(prev => ({ ...prev, weightChange: change }));
              }
            }
          }
        } catch (err) {}

      } catch (error) {
        console.error('Error loading client data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadClientData();
  }, [selectedClient]);

  useEffect(() => {
    if (openMealSelectIndex == null) return;
    const close = () => setOpenMealSelectIndex(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openMealSelectIndex]);

  // --- HELPER FORMATTERS ---
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'hebrew' ? 'he-IL' : 'en-US', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString(language === 'hebrew' ? 'he-IL' : 'en-US', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const parseMessageContent = (message) => {
    if (!message) return '';
    const content = message.message || message.content || '';
    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      if (parsed && typeof parsed === 'object' && parsed.response_text) {
        return parsed.response_text;
      }
    } catch (e) { }
    return content;
  };

  const parsePhoneNumber = (fullNumber) => {
    if (!fullNumber) return { code: '+972', number: '' };
    const found = countryCodes.find(c => fullNumber.startsWith(c.code));
    if (found) return { code: found.code, number: fullNumber.slice(found.code.length) };
    return { code: '+972', number: fullNumber.replace(/^\+/, '') }; 
  };

  // --- HANDLERS ---
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileFormData(prev => {
      const newData = { ...prev, [name]: value };

      if (name === 'activity_level') newData.Activity_level = value;
      if (name === 'weight') newData.weight_kg = value;
      if (name === 'height') newData.height_cm = value;

      if (name === 'date_of_birth' && value) {
        const birthDate = new Date(value);
        const today = new Date();
        let calculatedAge = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          calculatedAge--;
        }
        newData.age = calculatedAge;
      }

      if (['age', 'date_of_birth', 'gender', 'weight', 'height', 'activity_level', 'goal'].includes(name)) {
        const currentActivity = newData.activity_level; 
        const bmr = calculateBMR(newData.age, newData.gender, newData.weight, newData.height);
        if (bmr) {
          newData.base_daily_total_calories = Math.round(bmr);
          const tdee = bmr * getActivityMultiplier(currentActivity);
          const newTarget = Math.round(tdee * getGoalFactor(newData.goal));
          newData.daily_target_total_calories = newTarget;
          setTargetCalories(newTarget);
          setMacros(prevMacros => {
            const next = { ...prevMacros };
            ['protein', 'carbs', 'fats'].forEach((k) => { 
              next[k].grams = getGramsFromPercent(next[k].percent, k, newTarget); 
            });
            return next;
          });
        }
      }
      return newData;
    });
  };

  const handlePhoneChange = (type, value, fieldName) => {
    const { code, number } = parsePhoneNumber(profileFormData[fieldName]);
    let newFullNumber = type === 'code' ? `${value}${number}` : `${code}${value.replace(/\D/g, '')}`;
    setProfileFormData(prev => ({ ...prev, [fieldName]: newFullNumber }));
  };

  const toggleArrayItem = (field, itemValue) => {
    setProfileFormData(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(itemValue) ? arr.filter(v => v !== itemValue) : [...arr, itemValue] };
    });
  };

  const saveCurrentWeightToLog = async (userCode, token) => {
    const weightVal = parseFloat(currentWeightInput);
    if (isNaN(weightVal) || weightVal <= 0) return;
    const latestWeight = weightLogs.length > 0 ? weightLogs[0].weight_kg : null;
    if (latestWeight != null && latestWeight === weightVal) return;
    const today = new Date().toISOString().split('T')[0];
    const res = await fetch(`${API_BASE_URL}/weight-logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        userCode,
        weight_kg: weightVal,
        measurement_date: today,
      }),
    });
    const responseData = await res.json();
    if (res.ok && responseData.success) {
      const weightResponse = await fetch(`${API_BASE_URL}/weight-logs?userCode=${encodeURIComponent(userCode)}`);
      if (weightResponse.ok) {
        const weightData = await weightResponse.json();
        const logs = weightData.data || [];
        setWeightLogs(logs);
        if (logs.length >= 2) {
          const sorted = [...logs].sort((a, b) => new Date(a.measurement_date) - new Date(b.measurement_date));
          setStats(prev => ({ ...prev, weightChange: sorted[sorted.length - 1].weight_kg - sorted[0].weight_kg }));
        }
      }
    }
  };

  const handleSaveProfile = async () => {
    if (!clientData?.user_code) return;
    setIsSavingProfile(true);
    try {
      const token = await authClient.getAccessToken();
      if (!token) {
        alert('You must be logged in to save changes.');
        return;
      }

      const payload = { ...profileFormData };
      payload.food_allergies = payload.food_allergies.join(', '); 
      payload.daily_target_total_calories = targetCalories;
      const res = await fetch(`${API_BASE_URL}/chat-users/${clientData.user_code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload)
      });
      const responseData = await res.json();
      if (res.ok) {
        setClientData(prev => ({ ...prev, ...responseData }));
        if (token && clientData.user_code) {
          await saveCurrentWeightToLog(clientData.user_code, token);
        }
        setIsEditingProfile(false);
      } else {
        alert(responseData.error || "Failed to update profile.");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving profile.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  // --- PREFERENCES LOGIC (Ported) ---
  const sortedByDate = weightLogs.length > 0 ? [...weightLogs].sort((a, b) => new Date(a.measurement_date) - new Date(b.measurement_date)) : [];
  const firstWeightFromLogs = sortedByDate.length > 0 && sortedByDate[0] != null ? sortedByDate[0].weight_kg : null;
  const currentWeightFromLogs = weightLogs.length > 0 && weightLogs[0] != null ? weightLogs[0].weight_kg : null;
  const CLIENT_WEIGHT = (currentWeightFromLogs != null ? currentWeightFromLogs : (profileFormData.weight || clientData?.weight_kg || 75));
  const getGramsFromPercent = (percent, type, totalCals) => Math.round((totalCals * (percent / 100)) / CALORIES_PER_GRAM[type]);
  const getPercentFromGrams = (grams, type, totalCals) => totalCals ? parseFloat(((grams * CALORIES_PER_GRAM[type] / totalCals) * 100).toFixed(1)) : 0;
  const updateMacrosSafely = (changedKey, newPercentRaw, currentMacros, totalCals) => {
    let newPercent = Math.max(0, Math.min(100, parseFloat(newPercentRaw) || 0));
    const nextMacros = { ...currentMacros };
    const keys = ['protein', 'carbs', 'fats'];
    const lockedKey = keys.find(k => nextMacros[k].locked);
    if (lockedKey && lockedKey !== changedKey) {
      const lockedVal = nextMacros[lockedKey].percent;
      if (newPercent + lockedVal > 100) newPercent = 100 - lockedVal;
      const remainingKey = keys.find(k => k !== changedKey && k !== lockedKey);
      nextMacros[changedKey].percent = newPercent;
      nextMacros[remainingKey].percent = parseFloat((100 - lockedVal - newPercent).toFixed(1));
    } else {
      const otherKeys = keys.filter(k => k !== changedKey);
      const oldSumOthers = otherKeys.reduce((sum, k) => sum + nextMacros[k].percent, 0);
      nextMacros[changedKey].percent = newPercent;
      const remainingSpace = 100 - newPercent;
      otherKeys.forEach(k => {
        const ratio = oldSumOthers === 0 ? (1 / otherKeys.length) : (nextMacros[k].percent / oldSumOthers);
        nextMacros[k].percent = parseFloat((remainingSpace * ratio).toFixed(1));
      });
    }
    keys.forEach(k => { nextMacros[k].grams = getGramsFromPercent(nextMacros[k].percent, k, totalCals); });
    return nextMacros;
  };

  useEffect(() => {
    setMacros(prev => {
      const next = { ...prev };
      ['protein', 'carbs', 'fats'].forEach((k) => {
        next[k] = { ...prev[k], grams: getGramsFromPercent(prev[k].percent, k, targetCalories) };
      });
      return next;
    });
  }, [targetCalories]);

  const handleCaloriesChange = (val) => {
    const newCals = Math.max(0, parseInt(val, 10) || 0);
    setTargetCalories(newCals);
  };

  // Rescale a meal's stored grams to a new calorie target, preserving macro ratios.
  // Used when the dashboard redistributes calories among meals so the saved macros stay in sync.
  const rescaleMacrosForMeal = (mealMacros, newCals) => {
    if (!mealMacros) {
      return { protein: 0, carbs: 0, fats: 0 };
    }
    const currentCals = (mealMacros.protein || 0) * 4 + (mealMacros.carbs || 0) * 4 + (mealMacros.fats || 0) * 9;
    if (currentCals === 0 || !newCals) {
      const eachCals = (newCals || 0) / 3;
      return {
        protein: Math.max(0, Math.round(eachCals / 4)),
        carbs:   Math.max(0, Math.round(eachCals / 4)),
        fats:    Math.max(0, Math.round(eachCals / 9)),
      };
    }
    const k = newCals / currentCals;
    return {
      protein: Math.max(0, Math.round((mealMacros.protein || 0) * k)),
      carbs:   Math.max(0, Math.round((mealMacros.carbs   || 0) * k)),
      fats:    Math.max(0, Math.round((mealMacros.fats    || 0) * k)),
    };
  };

  const updateMealsSafely = (changedIndex, newCalValue) => {
    const newCals = Math.max(0, parseInt(newCalValue) || 0);
    const nextMeals = mealsList.map(m => ({ ...m, macros: m.macros ? { ...m.macros } : m.macros }));
    nextMeals[changedIndex].calories = newCals;
    nextMeals[changedIndex].macros = rescaleMacrosForMeal(nextMeals[changedIndex].macros, newCals);
    const otherMealsIndices = nextMeals.map((_, i) => i).filter(i => i !== changedIndex && !nextMeals[i].locked);
    const totalCalsOther = nextMeals.reduce((sum, m, i) => i !== changedIndex && nextMeals[i].locked ? sum + m.calories : sum, 0);
    const remainingSpace = targetCalories - newCals - totalCalsOther;
    if (otherMealsIndices.length > 0) {
      const currentSumOfOthers = otherMealsIndices.reduce((sum, i) => sum + nextMeals[i].calories, 0);
      otherMealsIndices.forEach(idx => {
        const ratio = currentSumOfOthers === 0 ? (1 / otherMealsIndices.length) : (nextMeals[idx].calories / currentSumOfOthers);
        const cals = Math.round(remainingSpace * ratio);
        nextMeals[idx].calories = cals;
        nextMeals[idx].macros = rescaleMacrosForMeal(nextMeals[idx].macros, cals);
      });
    }
    setMealsList(nextMeals);
  };

  const handleAddMeal = () => {
    const usedKeys = new Set(mealsList.map((m) => m.nameKey));
    const firstUnused = MEAL_OPTION_KEYS.find((k) => !usedKeys.has(k));
    if (!firstUnused) return;
    const unlockedMeals = mealsList.filter(m => !m.locked);
    const remainingCals = targetCalories - mealsList.filter(m => m.locked).reduce((sum, m) => sum + m.calories, 0);
    const newMealCals = unlockedMeals.length > 0 ? Math.round(remainingCals / (unlockedMeals.length + 1)) : Math.round(targetCalories / (mealsList.length + 1));
    const ratio = unlockedMeals.length / (unlockedMeals.length + 1);
    const nextMeals = mealsList.map(meal => {
      if (meal.locked) return meal;
      const cals = Math.round(meal.calories * ratio);
      return { ...meal, calories: cals, macros: rescaleMacrosForMeal(meal.macros, cals) };
    });
    // Initial macros for the new meal: proportional split of the daily totals.
    const newRatio = targetCalories > 0 ? newMealCals / targetCalories : 0;
    nextMeals.push({
      id: Date.now(),
      nameKey: firstUnused,
      description: '',
      calories: newMealCals,
      locked: false,
      macros: {
        protein: Math.max(0, Math.round((parseInt(macros.protein.grams) || 0) * newRatio)),
        carbs:   Math.max(0, Math.round((parseInt(macros.carbs.grams)   || 0) * newRatio)),
        fats:    Math.max(0, Math.round((parseInt(macros.fats.grams)    || 0) * newRatio)),
      },
    });
    setMealsList(nextMeals);
  };

  const handleDeleteMeal = (index) => {
    const deletedMeal = mealsList[index];
    const remainingMeals = mealsList.filter((_, i) => i !== index).map(m => ({ ...m, macros: m.macros ? { ...m.macros } : m.macros }));
    const unlockedMeals = remainingMeals.filter(m => !m.locked);
    if (unlockedMeals.length > 0) {
      const currentSum = unlockedMeals.reduce((sum, m) => sum + m.calories, 0);
      remainingMeals.forEach(meal => {
        if (!meal.locked && currentSum > 0) {
          meal.calories = Math.round((currentSum + deletedMeal.calories) * (meal.calories / currentSum));
          meal.macros = rescaleMacrosForMeal(meal.macros, meal.calories);
        }
      });
    }
    setMealsList(remainingMeals);
  };

  const handleSavePreferences = async () => {
    if (!selectedClient?.id) return;
    setIsSavingPreferences(true);
    try {
      const transformedMeals = mealsList.map(meal => {
        const slotName = mealTranslations.english[meal.nameKey] || meal.nameKey;
        // Use the per-meal macros we loaded/maintained; fall back to proportional split.
        const calRatio = targetCalories > 0 ? (meal.calories || 0) / targetCalories : 0;
        const fallback = {
          protein: Math.round((parseInt(macros.protein.grams) || 0) * calRatio),
          carbs:   Math.round((parseInt(macros.carbs.grams)   || 0) * calRatio),
          fats:    Math.round((parseInt(macros.fats.grams)    || 0) * calRatio),
        };
        const mm = meal.macros || fallback;
        return {
          mealName: meal.description || '',
          mealSlot: slotName,
          targetMacros: {
            protein: Math.max(0, Math.round(mm.protein || 0)),
            carbs:   Math.max(0, Math.round(mm.carbs   || 0)),
            fats:    Math.max(0, Math.round(mm.fats    || 0)),
          },
          targetCalories: meal.calories || 0,
        };
      });
      await updateClientSettings(selectedClient.id, {
        numberOfMeals: mealsList.length, 
        calories: targetCalories,
        macros: { protein: `${macros.protein.grams}g`, carbs: `${macros.carbs.grams}g`, fat: `${macros.fats.grams}g` },
        mealPlanStructure: transformedMeals
      });
      setDailyTargets({ calories: targetCalories, macros: { protein: macros.protein.grams, carbs: macros.carbs.grams, fats: macros.fats.grams } });
      setMealPlanStructure({ structure: transformedMeals });
      setIsEditingPreferences(false);
    } catch (error) {
      alert("Failed to save preferences");
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const translations = {
    english: {
      'dashboard.edit': 'Edit', 'dashboard.save': 'Save', 'dashboard.cancel': 'Cancel', 'dashboard.saving': 'Saving...',
      'dashboard.welcome': 'Welcome to Your Dashboard', 'dashboard.subtitle': 'Overview of your nutrition practice',
      'dashboard.clientProfile': 'Client Profile', 'dashboard.clientProfileSubtitle': 'Premium client information',
      'dashboard.personalDetails': 'Personal Details', 'dashboard.contactLocation': 'Contact & Location',
      'dashboard.physicalStats': 'Physical Stats', 'dashboard.dietary': 'Dietary & Medical', 'dashboard.mealTiming': 'Meal Time Window',
      'dashboard.basicInfo': 'Basic Information', 'dashboard.nutritionTargets': 'Nutrition Targets',
      'dashboard.contactInfo': 'Contact Information',
      'dashboard.name': 'Full Name', 'dashboard.preferredName': 'Preferred Name', 'dashboard.email': 'Email', 'dashboard.language': 'Language',
      'dashboard.clientCode': 'Client Code',
      'dashboard.phone': 'Phone', 'dashboard.whatsapp': 'WhatsApp', 'dashboard.city': 'City', 'dashboard.timezone': 'Timezone',
      'dashboard.dob': 'Date of Birth', 'dashboard.age': 'Age', 'dashboard.gender': 'Gender', 'dashboard.weight': 'Weight', 'dashboard.initialWeight': 'Initial weight', 'dashboard.currentWeight': 'Current weight', 'dashboard.startingWeight': 'Start', 'dashboard.currentWeightShort': 'Now', 'dashboard.measuredOn': 'Measured', 'dashboard.userCreatedAt': 'User since', 'dashboard.height': 'Height',
      'dashboard.bmi': 'BMI', 'dashboard.bmiUnderweight': 'Underweight', 'dashboard.bmiHealthy': 'Healthy', 'dashboard.bmiOverweight': 'Overweight', 'dashboard.bmiObese': 'Obese', 'dashboard.activityLevel': 'Activity Level', 'dashboard.goal': 'Goal', 'dashboard.targetCalories': 'Target Calories',
      'dashboard.targets': 'Targets', 'dashboard.calories': 'Calories', 'dashboard.maintenance': 'Maintenance', 'dashboard.tdeeHint': 'Total Daily Energy Expenditure — estimated daily calorie burn to maintain weight',
      'dashboard.bmrCalories': 'BMR Calories', 'dashboard.numberOfMeals': 'Number of Meals',
      'dashboard.foodAllergies': 'Food Allergies', 'dashboard.foodLimitations': 'Food Limitations', 'dashboard.dietaryRestrictions': 'Dietary Restrictions',
      'dashboard.recommendations': 'Recommendations / Notes', 'dashboard.region': 'Culinary Region', 'dashboard.firstMeal': 'First Meal Time', 
      'dashboard.lastMeal': 'Last Meal Time', 'dashboard.mealTimeWindow': 'Eating window', 'dashboard.macros': 'Macros', 'dashboard.mealPlanStructure': 'Meal Plan Structure',
      'dashboard.dayStructure': 'Day structure', 'dashboard.mealsCount': '{{count}} meals', 'dashboard.equalSplit': 'Equal split',
      'dashboard.content': 'Note', 'dashboard.lock': 'Lock', 'dashboard.unlock': 'Unlock', 'dashboard.totalCalories': 'Total',
      'dashboard.protein': 'Protein', 'dashboard.carbohydrates': 'Carbs', 'dashboard.fat': 'Fat', 'dashboard.kg': 'kg', 'dashboard.cm': 'cm', 
      'dashboard.kcal': 'kcal', 'dashboard.g': 'g', 'dashboard.na': 'N/A', 'dashboard.other': 'Other', 'dashboard.add': 'Add',
      'dashboard.clientPreferences': 'Client Preferences',
      'dashboard.totalMenus': 'Total Menus', 'dashboard.activeMenus': 'Active Menus', 'dashboard.chatSessions': 'Chat Sessions', 'dashboard.weightChange': 'Weight Change',
      'dashboard.quickActions': 'Quick Actions', 'dashboard.quickActionsSubtitle': 'Access your most common tasks quickly',
      'dashboard.createMealPlan': 'Create Meal Plan', 'dashboard.createMealPlanDesc': 'Generate a nutrition plan',
      'dashboard.startChat': 'Start Chat', 'dashboard.startChatDesc': 'Consult with AI assistant',
      'dashboard.mealPlanOverview': 'Meal Plan Overview', 'dashboard.mealPlanOverviewSubtitle': 'Recent nutrition plans and menus',
      'dashboard.noMealPlans': 'No meal plans yet', 'dashboard.createFirstMealPlan': 'Create your first meal plan',
      'dashboard.consultations': 'Consultations', 'dashboard.consultationsSubtitle': 'AI-powered nutrition consultations',
      'dashboard.noRecentActivity': 'No recent activity',
      'dashboard.viewFullChat': 'View Full Chat', 'dashboard.recentActivity': 'Recent Activity', 'dashboard.recentActivitySubtitle': 'Latest updates from your practice',
      'dashboard.weightLogs': 'Weight & Body Fat', 'dashboard.weightLogsSubtitle': 'Weight tracking history',
      'dashboard.noWeightLogs': 'No weight logs yet', 'dashboard.addFirstWeightLog': 'Add Your First Weight Log',
      'dashboard.customOnboarding': 'Custom Onboarding Questions', 'dashboard.customOnboardingNoAnswer': 'Not answered',
      'dashboard.customOnboardingEmpty': 'No custom onboarding questions configured for this company.',
      'dashboard.customOnboardingTypeText': 'Text', 'dashboard.customOnboardingTypeSelect': 'Single choice',
      'dashboard.customOnboardingTypeMultiselect': 'Multiple choice',
    },
    hebrew: {
      'dashboard.edit': 'ערוך', 'dashboard.save': 'שמור', 'dashboard.cancel': 'ביטול', 'dashboard.saving': 'שומר...',
      'dashboard.welcome': 'ברוכים הבאים לדשבורד שלך', 'dashboard.subtitle': 'סקירה כללית של פרקטיקת התזונה שלך',
      'dashboard.clientProfile': 'פרופיל לקוח', 'dashboard.clientProfileSubtitle': 'מידע פרמיום על הלקוח',
      'dashboard.personalDetails': 'פרטים אישיים', 'dashboard.contactLocation': 'יצירת קשר ומיקום',
      'dashboard.physicalStats': 'נתונים פיזיים', 'dashboard.dietary': 'תזונה ורפואה', 'dashboard.mealTiming': 'חלון זמני ארוחות',
      'dashboard.basicInfo': 'מידע בסיסי', 'dashboard.nutritionTargets': 'יעדי תזונה',
      'dashboard.contactInfo': 'פרטי קשר',
      'dashboard.name': 'שם מלא', 'dashboard.preferredName': 'שם מועדף', 'dashboard.email': 'אימייל', 'dashboard.language': 'שפת ממשק AI',
      'dashboard.clientCode': 'קוד לקוח',
      'dashboard.phone': 'טלפון', 'dashboard.whatsapp': 'ווטסאפ', 'dashboard.city': 'עיר', 'dashboard.timezone': 'אזור זמן',
      'dashboard.dob': 'תאריך לידה', 'dashboard.age': 'גיל', 'dashboard.gender': 'מין', 'dashboard.weight': 'משקל', 'dashboard.initialWeight': 'משקל התחלתי', 'dashboard.currentWeight': 'משקל נוכחי', 'dashboard.startingWeight': 'התחלה', 'dashboard.currentWeightShort': 'עכשיו', 'dashboard.measuredOn': 'נמדד', 'dashboard.userCreatedAt': 'משתמש מאז', 'dashboard.height': 'גובה',
      'dashboard.bmi': 'BMI', 'dashboard.bmiUnderweight': 'תת-משקל', 'dashboard.bmiHealthy': 'תקין', 'dashboard.bmiOverweight': 'עודף משקל', 'dashboard.bmiObese': 'השמנה', 'dashboard.activityLevel': 'רמת פעילות', 'dashboard.goal': 'מטרה', 'dashboard.targetCalories': 'קלוריות יעד',
      'dashboard.targets': 'יעדים', 'dashboard.calories': 'קלוריות', 'dashboard.maintenance': 'תחזוקה', 'dashboard.tdeeHint': 'הוצאת אנרגיה יומית כוללת — הערכת שריפת קלוריות יומית לשמירה על המשקל',
      'dashboard.bmrCalories': 'קלוריות BMR', 'dashboard.numberOfMeals': 'מספר ארוחות',
      'dashboard.foodAllergies': 'אלרגיות', 'dashboard.foodLimitations': 'הגבלות מזון', 'dashboard.dietaryRestrictions': 'הגבלות תזונתיות',
      'dashboard.recommendations': 'המלצות / הערות', 'dashboard.region': 'אזור קולינרי', 'dashboard.firstMeal': 'שעת ארוחה ראשונה', 
      'dashboard.lastMeal': 'שעת ארוחה אחרונה', 'dashboard.mealTimeWindow': 'חלון אכילה', 'dashboard.macros': 'מאקרו', 'dashboard.mealPlanStructure': 'מבנה תוכנית ארוחות',
      'dashboard.dayStructure': 'מבנה היום', 'dashboard.mealsCount': '{{count}} ארוחות', 'dashboard.equalSplit': 'חלוקה שווה',
      'dashboard.content': 'הערה', 'dashboard.lock': 'נעל', 'dashboard.unlock': 'שחרר', 'dashboard.totalCalories': 'סה״כ',
      'dashboard.protein': 'חלבון', 'dashboard.carbohydrates': 'פחמימות', 'dashboard.fat': 'שומן', 'dashboard.kg': 'ק"ג', 'dashboard.cm': 'ס"מ', 
      'dashboard.kcal': 'קק"ל', 'dashboard.g': 'גרם', 'dashboard.na': 'לא זמין', 'dashboard.other': 'אחר', 'dashboard.add': 'הוסף',
      'dashboard.clientPreferences': 'העדפות לקוח',
      'dashboard.totalMenus': 'סה"כ תפריטים', 'dashboard.activeMenus': 'תפריטים פעילים', 'dashboard.chatSessions': 'שיחות צ\'אט', 'dashboard.weightChange': 'שינוי משקל',
      'dashboard.quickActions': 'פעולות מהירות', 'dashboard.quickActionsSubtitle': 'גש למשימות הנפוצות ביותר שלך במהירות',
      'dashboard.createMealPlan': 'צור תוכנית ארוחות', 'dashboard.createMealPlanDesc': 'צור תוכנית תזונה',
      'dashboard.startChat': 'התחל צ\'אט', 'dashboard.startChatDesc': 'התייעץ עם עוזר AI',
      'dashboard.mealPlanOverview': 'סקירת תוכניות', 'dashboard.mealPlanOverviewSubtitle': 'תוכניות תזונה ותפריטים אחרונים',
      'dashboard.noMealPlans': 'אין עדיין תוכניות ארוחות', 'dashboard.createFirstMealPlan': 'צור תוכנית ארוחות ראשונה',
      'dashboard.consultations': 'ייעוצים', 'dashboard.consultationsSubtitle': 'ייעוצי תזונה מונעי AI',
      'dashboard.noRecentActivity': 'אין פעילות אחרונה',
      'dashboard.viewFullChat': 'צפה בצ\'אט המלא', 'dashboard.recentActivity': 'פעילות אחרונה', 'dashboard.recentActivitySubtitle': 'עדכונים אחרונים',
      'dashboard.weightLogs': 'יומני משקל', 'dashboard.weightLogsSubtitle': 'היסטוריית מעקב משקל',
      'dashboard.noWeightLogs': 'אין עדיין יומני משקל', 'dashboard.addFirstWeightLog': 'הוסף יומן משקל ראשון',
      'dashboard.customOnboarding': 'שאלות הצטרפות מותאמות', 'dashboard.customOnboardingNoAnswer': 'לא נענה',
      'dashboard.customOnboardingEmpty': 'לא הוגדרו שאלות הצטרפות מותאמות לחברה זו.',
      'dashboard.customOnboardingTypeText': 'טקסט', 'dashboard.customOnboardingTypeSelect': 'בחירה יחידה',
      'dashboard.customOnboardingTypeMultiselect': 'בחירה מרובה',
    }
  };
  const t = (key) => translations[language]?.[key] || translations.english[key] || key;
  const getCustomOnboardingTypeLabel = (type) => {
    if (type === 'multiselect') return t('dashboard.customOnboardingTypeMultiselect');
    if (type === 'select') return t('dashboard.customOnboardingTypeSelect');
    return t('dashboard.customOnboardingTypeText');
  };
  const getLabel = (options, val) => {
    if (val == null || val === '') return val;
    const goalAliases = { lose_weight: 'lose', gain_weight: 'gain', maintain_weight: 'maintain' };
    const normalized = goalAliases[val] || val;
    const option = options.find((o) => o.value === normalized || o.value === val);
    if (!option) {
      return String(val)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return option.labelHe && language === 'hebrew' ? option.labelHe : option.labelEn || option.label;
  };

  if (!selectedClient) {
    return (
      <ClientWorkspaceLayout>
        <div />
      </ClientWorkspaceLayout>
    );
  }
  if (loading) return <div className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center`}><p className={themeClasses.textPrimary}>Loading...</p></div>;

  const client = clientData || selectedClient;
  const heroCurrentWeight = currentWeightFromLogs != null
    ? currentWeightFromLogs
    : (profileFormData.weight || client.weight_kg || client.weight);
  const heroStartingWeight = firstWeightFromLogs != null
    ? firstWeightFromLogs
    : (client.weight_kg ?? client.weight ?? null);
  const bmi = calculateBMI(heroCurrentWeight, profileFormData.height || client.height_cm);
  const bmiValue = bmi != null ? parseFloat(bmi) : null;
  const bmiCategory = bmiValue == null || isNaN(bmiValue)
    ? null
    : bmiValue < 18.5
      ? { key: 'underweight', label: t('dashboard.bmiUnderweight'), color: 'text-progress' }
      : bmiValue < 25
        ? { key: 'healthy', label: t('dashboard.bmiHealthy'), color: 'text-forest-ondark' }
        : bmiValue < 30
          ? { key: 'overweight', label: t('dashboard.bmiOverweight'), color: 'text-amber-500' }
          : { key: 'obese', label: t('dashboard.bmiObese'), color: 'text-rose-500' };
  // Visual scale spans 15 → 40; segments sized to real BMI cutoffs
  const bmiScaleMin = 15;
  const bmiScaleMax = 40;
  const bmiMarkerPct = bmiValue == null || isNaN(bmiValue)
    ? null
    : Math.min(100, Math.max(0, ((bmiValue - bmiScaleMin) / (bmiScaleMax - bmiScaleMin)) * 100));
  const heroGoalValue = client.goal || profileFormData.goal;
  const heroGoalIcon = (() => {
    const goalKey = heroGoalValue === 'lose_weight' ? 'lose'
      : heroGoalValue === 'gain_weight' ? 'gain'
        : heroGoalValue === 'maintain_weight' ? 'maintain'
          : heroGoalValue;
    switch (goalKey) {
      case 'lose':
      case 'cut':
        return { Icon: TrendingDown, color: 'text-progress dark:text-progress', bg: 'bg-progress-faint dark:bg-progress/15' };
      case 'maintain':
        return { Icon: Minus, color: 'text-ink-muted dark:text-ink-muted', bg: 'bg-moss dark:bg-moss/60' };
      case 'gain':
        return { Icon: TrendingUp, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/40' };
      case 'muscle':
        return { Icon: Dumbbell, color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-100 dark:bg-violet-900/40' };
      case 'improve_performance':
        return { Icon: Flame, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-900/40' };
      case 'improve_health':
        return { Icon: Heart, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-100 dark:bg-rose-900/40' };
      default:
        return { Icon: Activity, color: 'text-forest-ondark dark:text-forest-ondark', bg: 'bg-forest-faint dark:bg-forest/15' };
    }
  })();
  const readMacros = dailyTargets?.macros || client.macros || {};
  const readStructure = toStructureArray(mealPlanStructure?.structure ?? client.meal_plan_structure);
  const prefProteinG = Number(readMacros.protein ?? readMacros.protein_g) || 0;
  const prefCarbsG = Number(readMacros.carbs ?? readMacros.carbohydrates ?? readMacros.carbs_g) || 0;
  const prefFatG = Number(readMacros.fats ?? readMacros.fat ?? readMacros.fats_g) || 0;
  const prefMacroCalTotal = (prefProteinG * 4) + (prefCarbsG * 4) + (prefFatG * 9);
  const prefProteinPct = prefMacroCalTotal > 0 ? (prefProteinG * 4) / prefMacroCalTotal * 100 : 0;
  const prefCarbsPct = prefMacroCalTotal > 0 ? (prefCarbsG * 4) / prefMacroCalTotal * 100 : 0;
  const prefFatPct = prefMacroCalTotal > 0 ? (prefFatG * 9) / prefMacroCalTotal * 100 : 0;
  // One number everywhere: the target the page works with (saved, or estimated when none is saved).
  const prefDisplayKcal = targetCalories;
  const mealsTotalCountLabel = (t('dashboard.mealsCount') || '').replace('{{count}}', String(readStructure.length));
  const editMealsCountLabel = (t('dashboard.mealsCount') || '').replace('{{count}}', String(mealsList.length));
  const mealsCalSum = mealsList.reduce((s, m) => s + (m.calories || 0), 0);
  const mealsCalMatched = Math.abs(mealsCalSum - targetCalories) <= 5;
  const customOnboardingItems = buildCustomOnboardingItems(
    client.onboarding_custom_steps,
    client.custom_answers
  );
  const showCustomOnboarding = customOnboardingItems.length > 0;
  const bmrForHero = calculateBMR(profileFormData.age || client.age, profileFormData.gender || client.gender, heroCurrentWeight, profileFormData.height || client.height_cm || client.height);
  const tdeeForHero = bmrForHero != null && !isNaN(bmrForHero) ? Math.round(bmrForHero * getActivityMultiplier(profileFormData.activity_level || client.Activity_level || client.activity_level || 'sedentary')) : null;

  const profileSectionProps = {
    expandedSection,
    setExpandedSection,
    isDarkMode,
    textMutedClass: themeClasses.textMuted,
  };

  return (
    <ClientWorkspaceLayout>
      <div className={`min-h-screen ${themeClasses.bgPrimary} transition-colors duration-300 pb-12 font-sans`} dir={direction}>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Welcome Section */}
          <PageHeader
            title={t('dashboard.welcome')}
            description={t('dashboard.subtitle')}
          />

        {/* Bio-Hero: identity + client metrics */}
        <div
          data-tour="playbook-client-hero"
          className={`${cardBase} mb-8 overflow-hidden`}
        >
          <div className="flex flex-col xl:flex-row xl:items-stretch">
            {/* Identity */}
            <div className="flex items-center gap-3.5 px-5 py-4 sm:px-6 shrink-0 xl:w-[15.5rem] 2xl:w-[17rem] border-b xl:border-b-0 xl:border-e border-line dark:border-line">
              <div className="w-12 h-12 rounded-full bg-forest-faint dark:bg-forest/15 flex items-center justify-center text-xl font-bold text-forest-ondark dark:text-forest shrink-0">
                {(client.full_name || '?').charAt(0)}
              </div>
              <div className="min-w-0">
                <h2 className={`text-base sm:text-lg font-bold ${themeClasses.textPrimary} leading-snug break-words`}>
                  {client.full_name || 'N/A'}
                </h2>
                <p className={`text-xs font-medium ${themeClasses.textMuted} mt-0.5 leading-snug break-words`}>
                  {profileFormData.age ?? client.age ?? '—'} {language === 'hebrew' ? 'שנים' : 'yrs'} · {client.gender || '—'}
                  {(() => {
                    const heightCm = profileFormData.height || client.height_cm || client.height;
                    return heightCm ? ` · ${heightCm} ${t('dashboard.cm')}` : '';
                  })()}
                  {client.city ? ` · ${client.city}` : ''}
                </p>
              </div>
            </div>

            {/* Metrics: Weight → Calories → BMI → Goal */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-px bg-moss/70 dark:bg-moss/60">
              {/* Weight: start + now */}
              <div className={`px-4 sm:px-5 py-4 flex flex-col justify-center items-center text-center ${themeClasses.bgCard}`}>
                <div className={`text-[10px] font-semibold uppercase tracking-wider ${themeClasses.textMuted} mb-2`}>
                  {t('dashboard.weight')}
                </div>
                <div className="flex items-end justify-center gap-4 flex-wrap">
                  <div>
                    <div className={`text-[10px] font-medium ${themeClasses.textMuted} leading-none mb-1`}>{t('dashboard.startingWeight')}</div>
                    <div className={`text-base sm:text-lg font-bold tabular-nums leading-none whitespace-nowrap ${themeClasses.textPrimary}`}>
                      {heroStartingWeight ?? '—'}
                      {heroStartingWeight != null && (
                        <span className={`ms-1 text-xs font-semibold ${themeClasses.textMuted}`}>{t('dashboard.kg')}</span>
                      )}
                    </div>
                  </div>
                  <span className={`text-xs pb-0.5 ${themeClasses.textMuted}`} aria-hidden>·</span>
                  <div>
                    <div className={`text-[10px] font-medium ${themeClasses.textMuted} leading-none mb-1`}>{t('dashboard.currentWeightShort')}</div>
                    <div className={`text-base sm:text-lg font-bold tabular-nums leading-none whitespace-nowrap ${themeClasses.textPrimary}`}>
                      {heroCurrentWeight ?? '—'}
                      {heroCurrentWeight != null && (
                        <span className={`ms-1 text-xs font-semibold ${themeClasses.textMuted}`}>{t('dashboard.kg')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Calories: target + maintenance */}
              <div className={`px-4 sm:px-5 py-4 flex flex-col justify-center items-center text-center ${themeClasses.bgCard}`} title={t('dashboard.tdeeHint')}>
                <div className={`text-[10px] font-semibold uppercase tracking-wider ${themeClasses.textMuted} mb-2`}>
                  {t('dashboard.calories')}
                </div>
                <div className="flex items-end justify-center gap-4 flex-wrap">
                  <div>
                    <div className={`text-[10px] font-medium ${themeClasses.textMuted} leading-none mb-1`}>{t('dashboard.targets')}</div>
                    <div className="text-base sm:text-lg font-bold tabular-nums leading-none whitespace-nowrap text-forest-ondark dark:text-forest-ondark">
                      {targetCalories != null ? Number(targetCalories).toLocaleString() : '—'}
                    </div>
                  </div>
                  <span className={`text-xs pb-0.5 ${themeClasses.textMuted}`} aria-hidden>·</span>
                  <div>
                    <div className={`text-[10px] font-medium ${themeClasses.textMuted} leading-none mb-1`}>{t('dashboard.maintenance')}</div>
                    <div className={`text-base sm:text-lg font-bold tabular-nums leading-none whitespace-nowrap ${themeClasses.textPrimary}`}>
                      {tdeeForHero != null ? tdeeForHero.toLocaleString() : '—'}
                    </div>
                  </div>
                </div>
              </div>

              {/* BMI + scale */}
              <div className={`px-4 sm:px-5 py-4 flex flex-col justify-center items-center text-center ${themeClasses.bgCard}`}>
                <div className="w-full flex items-center justify-center gap-2 mb-1.5">
                  <div className={`text-[10px] font-semibold uppercase tracking-wider ${themeClasses.textMuted}`}>
                    {t('dashboard.bmi')}
                  </div>
                  {bmiCategory ? (
                    <div className={`text-[10px] font-bold uppercase tracking-wide ${bmiCategory.color}`}>
                      {bmiCategory.label}
                    </div>
                  ) : (
                    <div className={`text-[10px] font-semibold ${themeClasses.textMuted}`}>—</div>
                  )}
                </div>

                <div className="w-full" dir="ltr">
                  <div className="relative pt-5 pb-0.5">
                    {bmiMarkerPct != null && (
                      <div
                        className="absolute top-0 z-10 flex flex-col items-center pointer-events-none"
                        style={{ left: `${bmiMarkerPct}%`, transform: 'translateX(-50%)' }}
                      >
                        <span className={`text-[11px] font-bold tabular-nums leading-none mb-0.5 ${bmiCategory?.color || themeClasses.textPrimary}`}>
                          {bmi}
                        </span>
                        <div className={`w-0 h-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent ${
                          bmiCategory?.key === 'underweight' ? 'border-t-sky-500'
                            : bmiCategory?.key === 'healthy' ? 'border-t-emerald-500'
                              : bmiCategory?.key === 'overweight' ? 'border-t-amber-500'
                                : 'border-t-rose-500'
                        }`} />
                      </div>
                    )}
                    <div className="h-2.5 rounded-full overflow-hidden flex ring-1 ring-black/5 dark:ring-white/10">
                      <div className="bg-progress dark:bg-progress" style={{ width: '14%' }} title={`${t('dashboard.bmiUnderweight')} < 18.5`} />
                      <div className="bg-forest-ondark dark:bg-forest-ondark" style={{ width: '26%' }} title={`${t('dashboard.bmiHealthy')} 18.5–24.9`} />
                      <div className="bg-amber-400 dark:bg-amber-500" style={{ width: '20%' }} title={`${t('dashboard.bmiOverweight')} 25–29.9`} />
                      <div className="bg-rose-400 dark:bg-rose-500" style={{ width: '40%' }} title={`${t('dashboard.bmiObese')} ≥ 30`} />
                    </div>
                  </div>

                  <div className={`relative h-3.5 mt-0.5 text-[9px] font-medium tabular-nums ${themeClasses.textMuted}`}>
                    {[
                      { label: '15', pct: 0, align: 'start' },
                      { label: '18.5', pct: 14, align: 'center' },
                      { label: '25', pct: 40, align: 'center' },
                      { label: '30', pct: 60, align: 'center' },
                      { label: '40', pct: 100, align: 'end' },
                    ].map((tick) => (
                      <span
                        key={tick.label}
                        className="absolute top-0"
                        style={{
                          left: `${tick.pct}%`,
                          transform: tick.align === 'start' ? 'none' : tick.align === 'end' ? 'translateX(-100%)' : 'translateX(-50%)',
                        }}
                      >
                        {tick.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Goal */}
              <div className={`px-4 sm:px-5 py-4 flex flex-col justify-center items-center text-center ${themeClasses.bgCard}`}>
                <div className={`text-[10px] font-semibold uppercase tracking-wider ${themeClasses.textMuted} mb-2`}>
                  {t('dashboard.goal')}
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${heroGoalIcon.bg} ${heroGoalIcon.color}`}>
                    <heroGoalIcon.Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className={`text-base sm:text-lg font-bold leading-snug break-words ${themeClasses.textPrimary}`}>
                    {getLabel(goalOptions, heroGoalValue) || '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- CLIENT PROFILE SECTION --- */}
        <div className={`${cardBase} p-5 sm:p-8 mb-8 transition-all duration-300 ${isEditingProfile ? (isDarkMode ? 'ring-2 ring-forest/20 bg-forest/15' : 'ring-2 ring-forest/15 bg-forest/30') : ''}`}>
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-xl sm:text-2xl font-bold ${themeClasses.textPrimary} flex items-center gap-2`}>
              <User className="text-forest-ondark" size={24} /> 
              {t('dashboard.clientProfile')}
            </h2>
            {!isEditingProfile ? (
              <button type="button" onClick={() => setIsEditingProfile(true)} className={themeClasses.btnSecondaryPill}>
                <Edit2 size={16} /> {t('dashboard.edit')}
              </button>
            ) : (
              <div className="flex gap-2">
                <button type="button" onClick={() => setIsEditingProfile(false)} className={themeClasses.btnMutedPill}>{t('dashboard.cancel')}</button>
                <button type="button" onClick={handleSaveProfile} disabled={isSavingProfile} className={themeClasses.btnPrimaryPill + ' min-w-[100px]'}>
                  {isSavingProfile ? (
                    <Activity size={18} className="animate-spin" />
                  ) : (
                    <Save size={18} />
                  )}
                  {isSavingProfile ? t('dashboard.saving') : t('dashboard.save')}
                </button>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <ProfileSection {...profileSectionProps} tourAnchor="playbook-client-profile" title={t('dashboard.personalDetails')} sectionKey="personal" icon={<User size={18} />}>
              <div className={detailGridClass}>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <User size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.name')}
                  </label>
                  {isEditingProfile ? <input name="full_name" value={profileFormData.full_name} onChange={handleProfileChange} className={themeClasses.inputField} /> : <p className={detailValueClass}>{client.full_name || 'N/A'}</p>}
                </div>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <UserCircle size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.preferredName')}
                  </label>
                  {isEditingProfile ? <input name="preferred_name" value={profileFormData.preferred_name} onChange={handleProfileChange} className={themeClasses.inputField} /> : <p className={detailValueClass}>{client.preferred_name || 'N/A'}</p>}
                </div>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <Languages size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.language')}
                  </label>
                  {isEditingProfile ? <select name="language" value={profileFormData.language} onChange={handleProfileChange} className={themeClasses.select}>{aiLanguages.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}</select> : <p className={detailValueClass}>{aiLanguages.find(l => l.value === client.language)?.label || client.language || 'N/A'}</p>}
                </div>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <Calendar size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.dob')}
                  </label>
                  {isEditingProfile ? <input name="date_of_birth" type="date" value={profileFormData.date_of_birth} onChange={handleProfileChange} className={themeClasses.inputField} /> : <p className={detailValueClass}>{client.date_of_birth || 'N/A'}</p>}
                </div>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <Cake size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.age')}
                  </label>
                  {isEditingProfile ? <input name="age" type="number" value={profileFormData.age} onChange={handleProfileChange} className={themeClasses.inputField} /> : <p className={detailValueClass}>{client.age || 'N/A'}</p>}
                </div>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <VenusAndMars size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.gender')}
                  </label>
                  {isEditingProfile ? <select name="gender" value={profileFormData.gender} onChange={handleProfileChange} className={themeClasses.select}><option value="">{t('dashboard.na')}</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select> : <p className={detailValueClass}>{client.gender || 'N/A'}</p>}
                </div>
              </div>
            </ProfileSection>

            <ProfileSection {...profileSectionProps} title={t('dashboard.contactLocation')} sectionKey="contact" icon={<Smartphone size={18} />}>
              <div className={detailGridClass}>
                <div className={`${detailTile} sm:col-span-2 xl:col-span-3`}>
                  <label className={detailLabelClass}>
                    <Mail size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.email')}
                  </label>
                  {isEditingProfile ? <input name="email" value={profileFormData.email} onChange={handleProfileChange} className={themeClasses.inputField} /> : <p className={`${detailValueClass} break-all`}>{client.email || 'N/A'}</p>}
                </div>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <Phone size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.phone')}
                  </label>
                  {isEditingProfile ? (
                    <div className="flex gap-2" dir="ltr">
                      <select value={parsePhoneNumber(profileFormData.phone_number).code} onChange={(e) => handlePhoneChange('code', e.target.value, 'phone_number')} className={`w-[100px] shrink-0 ${themeClasses.select} py-2 text-xs`}>{countryCodes.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}</select>
                      <input type="text" value={parsePhoneNumber(profileFormData.phone_number).number} onChange={(e) => handlePhoneChange('number', e.target.value, 'phone_number')} className={`flex-1 ${themeClasses.inputField}`} />
                    </div>
                  ) : <p className={detailValueClass}>{client.phone_number || 'N/A'}</p>}
                </div>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <MessageCircle size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.whatsapp')}
                  </label>
                  {isEditingProfile ? (
                    <div className="flex gap-2" dir="ltr">
                      <select value={parsePhoneNumber(profileFormData.whatsapp_number).code} onChange={(e) => handlePhoneChange('code', e.target.value, 'whatsapp_number')} className={`w-[100px] shrink-0 ${themeClasses.select} py-2 text-xs`}>{countryCodes.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}</select>
                      <input type="text" value={parsePhoneNumber(profileFormData.whatsapp_number).number} onChange={(e) => handlePhoneChange('number', e.target.value, 'whatsapp_number')} className={`flex-1 ${themeClasses.inputField}`} />
                    </div>
                  ) : <p className={detailValueClass}>{client.whatsapp_number || 'N/A'}</p>}
                </div>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <MapPin size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.city')}
                  </label>
                  {isEditingProfile ? <input name="city" value={profileFormData.city} onChange={handleProfileChange} className={themeClasses.inputField} /> : <p className={detailValueClass}>{client.city || 'N/A'}</p>}
                </div>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <Globe size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.timezone')}
                  </label>
                  {isEditingProfile ? <select name="timezone" value={profileFormData.timezone} onChange={handleProfileChange} className={themeClasses.select}><option value="">{t('dashboard.na')}</option>{timezones.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}</select> : <p className={detailValueClass}>{timezones.find(t=>t.value===client.timezone)?.label || client.timezone || 'N/A'}</p>}
                </div>
                <div className={detailTile}>
                  <label className={detailLabelClass}>
                    <Map size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.region')}
                  </label>
                  {isEditingProfile ? <select name="region" value={profileFormData.region} onChange={handleProfileChange} className={themeClasses.select}>{regions.map(r => <option key={r.value} value={r.value}>{getLabel(regions, r.value)}</option>)}</select> : <p className={detailValueClass}>{getLabel(regions, client.region) || 'N/A'}</p>}
                </div>
              </div>
            </ProfileSection>

            <ProfileSection {...profileSectionProps} title={t('dashboard.physicalStats')} sectionKey="physical" icon={<Scale size={18} />}>
              <div className={detailGridClass}>
                <div className={`${detailTile} h-full`}>
                  <label className={detailLabelClass}>
                    <Scale size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.initialWeight')} ({t('dashboard.kg')})
                  </label>
                  <p className={`min-h-[1.1rem] text-[11px] leading-tight ${themeClasses.textMuted}`}>
                    {sortedByDate.length > 0 && sortedByDate[0].measurement_date
                      ? `${t('dashboard.measuredOn')}: ${formatDate(sortedByDate[0].measurement_date)}`
                      : sortedByDate.length === 0 && client.created_at
                        ? `${t('dashboard.userCreatedAt')}: ${formatDateTime(client.created_at)}`
                        : '\u00a0'}
                  </p>
                  <p className={`mt-auto text-sm font-semibold tabular-nums ${themeClasses.textPrimary}`}>
                    {firstWeightFromLogs != null ? firstWeightFromLogs : (client.weight_kg ?? client.weight ?? 'N/A')}
                  </p>
                </div>
                <div className={`${detailTile} h-full`}>
                  <label className={detailLabelClass}>
                    <Activity size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.currentWeight')} ({t('dashboard.kg')})
                  </label>
                  <p className={`min-h-[1.1rem] text-[11px] leading-tight ${themeClasses.textMuted}`}>
                    {weightLogs.length > 0 && weightLogs[0].measurement_date
                      ? `${t('dashboard.measuredOn')}: ${formatDate(weightLogs[0].measurement_date)}`
                      : '\u00a0'}
                  </p>
                  {isEditingProfile ? (
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={currentWeightInput}
                      onChange={(e) => setCurrentWeightInput(e.target.value)}
                      className={`${themeClasses.inputField} mt-auto tabular-nums`}
                      placeholder="—"
                    />
                  ) : (
                    <p className={`mt-auto text-sm font-semibold tabular-nums ${themeClasses.textPrimary}`}>
                      {currentWeightFromLogs != null ? currentWeightFromLogs : '—'}
                    </p>
                  )}
                </div>
                <div className={`${detailTile} h-full`}>
                  <label className={detailLabelClass}>
                    <Ruler size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.height')} ({t('dashboard.cm')})
                  </label>
                  <p className={`min-h-[1.1rem] text-[11px] leading-tight ${themeClasses.textMuted}`}>{'\u00a0'}</p>
                  {isEditingProfile ? (
                    <input name="height" type="number" value={profileFormData.height} onChange={handleProfileChange} className={`${themeClasses.inputField} mt-auto tabular-nums`} />
                  ) : (
                    <p className={`mt-auto text-sm font-semibold tabular-nums ${themeClasses.textPrimary}`}>
                      {client.height_cm || client.height || 'N/A'}
                    </p>
                  )}
                </div>
                <div className={`${detailTile} h-full`}>
                  <label className={detailLabelClass}>
                    <Heart size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.bmi')}
                  </label>
                  <p className={`min-h-[1.1rem] text-[11px] leading-tight font-semibold ${bmiCategory?.color || themeClasses.textMuted}`}>
                    {bmiCategory?.label || '\u00a0'}
                  </p>
                  <p className={`mt-auto text-sm font-semibold tabular-nums ${bmiCategory?.color || themeClasses.textPrimary}`}>
                    {bmi || 'N/A'}
                  </p>
                </div>
                <div className={`${detailTile} h-full`}>
                  <label className={detailLabelClass}>
                    <Dumbbell size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.activityLevel')}
                  </label>
                  <p className={`min-h-[1.1rem] text-[11px] leading-tight ${themeClasses.textMuted}`}>{'\u00a0'}</p>
                  {isEditingProfile ? (
                    <select name="activity_level" value={profileFormData.activity_level} onChange={handleProfileChange} className={`${themeClasses.select} mt-auto`}>{activityLevelOptions.map(opt => <option key={opt.value} value={opt.value}>{getLabel(activityLevelOptions, opt.value)}</option>)}</select>
                  ) : (
                    <p className={`mt-auto text-sm font-semibold ${themeClasses.textPrimary}`}>
                      {getLabel(activityLevelOptions, client.Activity_level || client.activity_level) || 'N/A'}
                    </p>
                  )}
                </div>
                <div className={`${detailTile} h-full`}>
                  <label className={detailLabelClass}>
                    <Target size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.goal')}
                  </label>
                  <p className={`min-h-[1.1rem] text-[11px] leading-tight ${themeClasses.textMuted}`}>{'\u00a0'}</p>
                  {isEditingProfile ? (
                    <select name="goal" value={profileFormData.goal} onChange={handleProfileChange} className={`${themeClasses.select} mt-auto`}>{goalOptions.map(opt => <option key={opt.value} value={opt.value}>{getLabel(goalOptions, opt.value)}</option>)}</select>
                  ) : (
                    <p className={`mt-auto text-sm font-semibold ${themeClasses.textPrimary}`}>
                      {getLabel(goalOptions, client.goal) || 'N/A'}
                    </p>
                  )}
                </div>
                <div className={`${detailTile} h-full`}>
                  <label className={detailLabelClass}>
                    <Flame size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.targetCalories')}
                  </label>
                  <p className={`min-h-[1.1rem] text-[11px] leading-tight ${themeClasses.textMuted}`}>{'\u00a0'}</p>
                  {isEditingProfile ? (
                    <input type="number" value={targetCalories} onChange={(e) => setTargetCalories(parseInt(e.target.value) || 0)} className={`${themeClasses.inputField} mt-auto tabular-nums`} />
                  ) : (
                    <p className={`mt-auto text-sm font-semibold tabular-nums ${themeClasses.textPrimary}`}>
                      {targetCalories} {t('dashboard.kcal')}
                    </p>
                  )}
                </div>
                <div className={`${detailTile} h-full`}>
                  <label className={detailLabelClass}>
                    <Flame size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.bmrCalories')}
                  </label>
                  <p className={`min-h-[1.1rem] text-[11px] leading-tight ${themeClasses.textMuted}`}>{'\u00a0'}</p>
                  <p className={`mt-auto text-sm font-semibold tabular-nums ${themeClasses.textPrimary}`}>
                    {profileFormData.base_daily_total_calories || client.base_daily_total_calories || 'N/A'} {t('dashboard.kcal')}
                  </p>
                </div>
              </div>
            </ProfileSection>

            <ProfileSection {...profileSectionProps} title={t('dashboard.dietary')} sectionKey="dietary" icon={<Leaf size={18} />}>
              <div className={detailGridClass}>
                <div className={`${detailTile} sm:col-span-2 xl:col-span-3`}>
                  <label className={detailLabelClass}>
                    <AlertTriangle size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.foodAllergies')}
                  </label>
                  {isEditingProfile ? (
                    <div className={`rounded-lg border p-3 ${isDarkMode ? 'border-line bg-surface/40' : 'border-line bg-surface'}`}>
                      <div className="flex flex-wrap gap-2">
                        {allergiesOptions.map(opt => (
                          <button key={opt.value} type="button" onClick={() => toggleArrayItem('food_allergies', opt.value)} className={`px-4 py-1.5 text-xs font-medium rounded-lg border  transition-all duration-200 active:scale-95 hover:ring-2 hover:ring-forest/30 ${profileFormData.food_allergies?.includes(opt.value) ? 'bg-red-500 text-white border-red-500' : `${isDarkMode ? 'bg-surface border-line text-ink hover:border-forest/50' : 'bg-canvas border-line text-ink hover:border-forest/40'}`}`}>
                            {getLabel(allergiesOptions, opt.value)}
                          </button>
                        ))}
                        {profileFormData.food_allergies?.filter(val => !allergiesOptions.find(o=>o.value===val)).map(customVal => (
                          <button key={customVal} type="button" onClick={() => toggleArrayItem('food_allergies', customVal)} className="px-4 py-1.5 text-xs font-medium rounded-lg border  bg-red-500 text-white border-red-500 transition-all duration-200 active:scale-95 flex items-center gap-1">
                            {customVal} <X size={14}/>
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-4 pt-4 border-t border-line dark:border-line">
                        <input value={customAllergyInput} onChange={e=>setCustomAllergyInput(e.target.value)} placeholder={t('dashboard.other')} className={`flex-1 min-w-0 ${themeClasses.inputField} py-2`} />
                        <button type="button" onClick={() => { if(customAllergyInput) { toggleArrayItem('food_allergies', customAllergyInput); setCustomAllergyInput(''); } }} className={themeClasses.btnSecondaryPill + ' shrink-0'}>{t('dashboard.add')}</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {profileFormData.food_allergies?.length > 0 ? profileFormData.food_allergies.map(val => <span key={val} className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-xs font-medium border border-red-200 dark:border-red-800/50">{getLabel(allergiesOptions, val)}</span>) : <span className={detailValueClass}>None</span>}
                    </div>
                  )}
                </div>

                <div className={`${detailTile} sm:col-span-2 xl:col-span-3`}>
                  <label className={detailLabelClass}>
                    <Ban size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.foodLimitations')}
                  </label>
                  {isEditingProfile ? (
                    <div className={`rounded-lg border p-3 ${isDarkMode ? 'border-line bg-surface/40' : 'border-line bg-surface'}`}>
                      <div className="flex flex-wrap gap-2">
                        {limitationsOptions.map(opt => (
                          <button key={opt.value} type="button" onClick={() => toggleArrayItem('food_limitations', opt.value)} className={`px-4 py-1.5 text-xs font-medium rounded-lg border  transition-all duration-200 active:scale-95 hover:ring-2 hover:ring-forest/30 ${profileFormData.food_limitations?.includes(opt.value) ? 'bg-amber-500 text-white border-amber-500' : `${isDarkMode ? 'bg-surface border-line text-ink hover:border-forest/50' : 'bg-canvas border-line text-ink hover:border-forest/40'}`}`}>
                            {getLabel(limitationsOptions, opt.value)}
                          </button>
                        ))}
                        {profileFormData.food_limitations?.filter(val => !limitationsOptions.find(o=>o.value===val)).map(customVal => (
                          <button key={customVal} type="button" onClick={() => toggleArrayItem('food_limitations', customVal)} className="px-4 py-1.5 text-xs font-medium rounded-lg border  bg-amber-500 text-white border-amber-500 transition-all duration-200 active:scale-95 flex items-center gap-1">
                            {customVal} <X size={14}/>
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-4 pt-4 border-t border-line dark:border-line">
                        <input value={customLimitationInput} onChange={e=>setCustomLimitationInput(e.target.value)} placeholder={t('dashboard.other')} className={`flex-1 min-w-0 ${themeClasses.inputField} py-2`} />
                        <button type="button" onClick={() => { if(customLimitationInput) { toggleArrayItem('food_limitations', customLimitationInput); setCustomLimitationInput(''); } }} className={themeClasses.btnSecondaryPill + ' shrink-0'}>{t('dashboard.add')}</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {profileFormData.food_limitations?.length > 0 ? profileFormData.food_limitations.map(val => <span key={val} className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-lg text-xs font-medium border border-amber-200 dark:border-amber-800/50">{getLabel(limitationsOptions, val)}</span>) : <span className={detailValueClass}>None</span>}
                    </div>
                  )}
                </div>

                <div className={`${detailTile} sm:col-span-2 xl:col-span-3`}>
                  <label className={detailLabelClass}>
                    <NotebookPen size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.recommendations')}
                  </label>
                  {isEditingProfile ? <textarea name="recommendations" value={profileFormData.recommendations} onChange={handleProfileChange} rows="4" className={`${themeClasses.inputField} resize-y min-h-[100px]`} /> : <p className={`${detailValueClass} whitespace-pre-wrap font-medium`}>{client.recommendations || 'None'}</p>}
                </div>
              </div>
            </ProfileSection>

            <ProfileSection {...profileSectionProps} title={t('dashboard.mealTiming')} sectionKey="mealTiming" icon={<Clock size={18} />}>
              <div className={detailGridClass}>
                <div className={`${detailTile} col-span-full`}>
                  <label className={detailLabelClass}>
                    <Clock size={12} className="opacity-70 shrink-0" aria-hidden />
                    {t('dashboard.mealTimeWindow')}
                  </label>
                  {isEditingProfile ? (
                    <div className="flex items-center gap-2 flex-wrap w-full" dir="ltr">
                      <input name="first_meal_time" type="time" value={profileFormData.first_meal_time} onChange={handleProfileChange} className={`${themeClasses.inputField} flex-1 min-w-[8rem] tabular-nums`} />
                      <span className={`${themeClasses.textMuted} text-sm font-semibold shrink-0`} aria-hidden>—</span>
                      <input name="last_meal_time" type="time" value={profileFormData.last_meal_time} onChange={handleProfileChange} className={`${themeClasses.inputField} flex-1 min-w-[8rem] tabular-nums`} />
                    </div>
                  ) : (
                    <p className={`${detailValueClass} tabular-nums`} dir="ltr">
                      {(client.first_meal_time || client.last_meal_time)
                        ? `${client.first_meal_time || '—'} – ${client.last_meal_time || '—'}`
                        : 'N/A'}
                    </p>
                  )}
                </div>
              </div>
            </ProfileSection>

            {showCustomOnboarding && (
              <ProfileSection {...profileSectionProps} title={t('dashboard.customOnboarding')} sectionKey="customOnboarding" icon={<ClipboardList size={18} />}>
                <div className={detailGridClass}>
                  {customOnboardingItems.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      className={`${detailTile} ${item.type === 'multiselect' || (item.answer && String(item.answer).length > 80) ? 'sm:col-span-2 xl:col-span-3' : ''}`}
                    >
                      <label className={detailLabelClass}>
                        <ClipboardList size={12} className="opacity-70 shrink-0" aria-hidden />
                        {item.question}
                      </label>
                      {item.description && (
                        <p className={`text-[11px] ${themeClasses.textMuted}`}>{item.description}</p>
                      )}
                      <span className={`${themeClasses.textMuted} text-[10px] font-semibold uppercase tracking-wider`}>
                        {getCustomOnboardingTypeLabel(item.type)}
                      </span>
                      {item.type === 'multiselect' && item.answerValues?.length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-0.5">
                          {item.answerValues.map((val) => (
                            <span
                              key={val}
                              className={`text-xs font-medium px-2.5 py-1 rounded-lg border ${isDarkMode ? 'bg-forest/15 text-forest border-forest/50' : 'bg-forest-faint text-forest-ondark border-forest/40'}`}
                            >
                              {val}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className={`text-sm font-semibold ${item.answer ? themeClasses.textPrimary : themeClasses.textMuted}`}>
                          {item.answer || t('dashboard.customOnboardingNoAnswer')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </ProfileSection>
            )}

          </div>
        </div>

        {/* --- CLIENT PREFERENCES --- */}
        <div className={`${cardBase} p-5 sm:p-8 mb-8`}>
          <div className="flex justify-between items-center gap-3 mb-8">
            <h2 className={`text-xl sm:text-2xl font-semibold tracking-tight ${themeClasses.textPrimary}`}>
              {t('dashboard.clientPreferences')}
            </h2>
            {!isEditingPreferences ? (
              <button onClick={() => setIsEditingPreferences(true)} className={`${themeClasses.btnMutedPill} !px-3`}>
                <Edit2 size={15} /> {t('dashboard.edit')}
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => setIsEditingPreferences(false)} className={themeClasses.btnMutedPill}>{t('dashboard.cancel')}</button>
                <button onClick={handleSavePreferences} disabled={isSavingPreferences} className={themeClasses.btnPrimaryPill + ' min-w-[100px]'}>
                  {isSavingPreferences ? <Activity size={18} className="animate-spin" /> : <Save size={18}/>}
                  {isSavingPreferences ? t('dashboard.saving') : t('dashboard.save')}
                </button>
              </div>
            )}
          </div>

          {!isEditingPreferences ? (
            <div className="space-y-10">
              {/* Macro strip */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                  <div>
                    <p className={`${themeClasses.textMuted} text-[11px] font-semibold uppercase tracking-wider mb-1`}>{t('dashboard.targetCalories')}</p>
                    <p className={`text-4xl sm:text-5xl font-semibold tracking-tight tabular-nums ${themeClasses.textPrimary}`}>
                      {Number(prefDisplayKcal || 0).toLocaleString()}
                      <span className={`ml-2 text-sm font-medium ${themeClasses.textMuted}`}>{t('dashboard.kcal')}</span>
                    </p>
                  </div>
                  <div className={`flex items-stretch gap-0 divide-x ${isDarkMode ? 'divide-line' : 'divide-line'}`}>
                    {[
                      { key: 'P', label: t('dashboard.protein'), grams: prefProteinG },
                      { key: 'C', label: t('dashboard.carbohydrates'), grams: prefCarbsG },
                      { key: 'F', label: t('dashboard.fat'), grams: prefFatG },
                    ].map((m) => (
                      <div key={m.key} className="px-4 sm:px-5 first:pl-0 last:pr-0">
                        <p className={`${themeClasses.textMuted} text-[11px] font-medium`}>{m.key}</p>
                        <p className={`text-lg font-semibold tabular-nums ${themeClasses.textPrimary}`}>
                          {m.grams}<span className={`text-xs font-medium ${themeClasses.textMuted}`}>{t('dashboard.g')}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={`mt-5 h-1.5 w-full rounded-full overflow-hidden flex ${isDarkMode ? 'bg-surface' : 'bg-moss'}`}>
                  <div className="h-full bg-forest-ondark dark:bg-forest-ondark" style={{ width: `${prefProteinPct}%` }} title={t('dashboard.protein')} />
                  <div className="h-full bg-forest/80" style={{ width: `${prefCarbsPct}%` }} title={t('dashboard.carbohydrates')} />
                  <div className="h-full bg-forest/70 dark:bg-forest/80" style={{ width: `${prefFatPct}%` }} title={t('dashboard.fat')} />
                </div>
              </div>

              {/* Day structure */}
              <div>
                <div className="flex items-baseline justify-between gap-3 mb-4">
                  <h3 className={`text-sm font-semibold ${themeClasses.textPrimary}`}>{t('dashboard.dayStructure')}</h3>
                  <span className={`text-xs ${themeClasses.textMuted}`}>{mealsTotalCountLabel}</span>
                </div>
                {readStructure.length > 0 ? (
                  <ul className={`divide-y ${isDarkMode ? 'divide-line' : 'divide-line'}`}>
                    {readStructure.map((item, index) => {
                      const slotName = item.mealSlot ?? item.meal;
                      const description = item.mealName ?? item.description;
                      const calories = item.targetCalories ?? item.calories;
                      return (
                        <li key={index} className="py-3.5 flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <p className={`text-sm font-medium ${themeClasses.textPrimary}`}>{getMealLabel(slotName)}</p>
                            {description ? (
                              <p className={`text-xs mt-0.5 ${themeClasses.textMuted} truncate`}>{description}</p>
                            ) : null}
                          </div>
                          <p className={`text-sm font-medium tabular-nums shrink-0 ${themeClasses.textPrimary}`}>
                            {calories}<span className={`ml-1 text-xs font-normal ${themeClasses.textMuted}`}>{t('dashboard.kcal')}</span>
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className={`text-sm ${themeClasses.textMuted}`}>{t('dashboard.na')}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-10">
              {/* Edit: calories hero */}
              <div>
                <label className={`${themeClasses.textMuted} text-[11px] font-semibold uppercase tracking-wider block mb-2`}>{t('dashboard.targetCalories')}</label>
                <div className="flex items-baseline gap-2">
                  <input
                    type="number"
                    value={targetCalories}
                    onChange={(e) => handleCaloriesChange(e.target.value)}
                    className={`${themeClasses.inputField} !text-3xl sm:!text-4xl !font-semibold !tracking-tight tabular-nums max-w-[12rem] !py-2`}
                  />
                  <span className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('dashboard.kcal')}</span>
                </div>
                <div className={`mt-5 h-1.5 w-full rounded-full overflow-hidden flex ${isDarkMode ? 'bg-surface' : 'bg-moss'}`}>
                  <div className="h-full bg-forest-ondark dark:bg-forest-ondark transition-all" style={{ width: `${macros.protein.percent}%` }} />
                  <div className="h-full bg-forest/80 transition-all" style={{ width: `${macros.carbs.percent}%` }} />
                  <div className="h-full bg-forest/70 dark:bg-forest/80 transition-all" style={{ width: `${macros.fats.percent}%` }} />
                </div>
              </div>

              {/* Edit: macro rows */}
              <div className={`divide-y ${isDarkMode ? 'divide-line' : 'divide-line'}`}>
                {['protein', 'carbs', 'fats'].map((type) => {
                  const m = macros[type];
                  const gPerKg = CLIENT_WEIGHT > 0 ? (m.grams / CLIENT_WEIGHT).toFixed(3) : '0.000';
                  const label = t(`dashboard.${type === 'fats' ? 'fat' : type === 'carbs' ? 'carbohydrates' : type}`);
                  return (
                    <div key={type} className="py-5 first:pt-0 last:pb-0">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <span className={`text-sm font-semibold ${themeClasses.textPrimary}`}>{label}</span>
                        <button
                          type="button"
                          onClick={() => setMacros(prev => ({ ...prev, [type]: { ...prev[type], locked: !prev[type].locked } }))}
                          className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${m.locked ? 'text-amber-600 dark:text-amber-400' : `${themeClasses.textMuted} hover:text-ink dark:hover:text-ink`}`}
                        >
                          {m.locked ? <Lock size={13} /> : <Unlock size={13} />}
                          {m.locked ? t('dashboard.lock') : t('dashboard.unlock')}
                        </button>
                      </div>
                      <div className="grid grid-cols-3 lg:grid-cols-12 gap-3 items-end">
                        <div className="lg:col-span-2">
                          <label className={`text-[10px] font-medium ${themeClasses.textMuted} block mb-1`}>%</label>
                          <input type="number" disabled={m.locked} value={m.percent} onChange={(e) => setMacros(prev => updateMacrosSafely(type, e.target.value, prev, targetCalories))} className={`${themeClasses.inputField} tabular-nums ${m.locked ? 'opacity-50' : ''}`} />
                        </div>
                        <div className="lg:col-span-2">
                          <label className={`text-[10px] font-medium ${themeClasses.textMuted} block mb-1`}>g</label>
                          <input type="number" disabled={m.locked} value={m.grams} onChange={(e) => { const newGrams = parseInt(e.target.value) || 0; setMacros(prev => updateMacrosSafely(type, getPercentFromGrams(newGrams, type, targetCalories), prev, targetCalories)); }} className={`${themeClasses.inputField} tabular-nums ${m.locked ? 'opacity-50' : ''}`} />
                        </div>
                        <div className="lg:col-span-2">
                          <label className={`text-[10px] font-medium ${themeClasses.textMuted} block mb-1`}>g/kg</label>
                          <input type="number" step="0.001" disabled={m.locked} value={gPerKg} onChange={(e) => { const newGrams = Math.round((parseFloat(e.target.value) || 0) * CLIENT_WEIGHT); setMacros(prev => updateMacrosSafely(type, getPercentFromGrams(newGrams, type, targetCalories), prev, targetCalories)); }} className={`${themeClasses.inputField} tabular-nums ${m.locked ? 'opacity-50' : ''}`} />
                        </div>
                        <div className="col-span-3 lg:col-span-6 pb-2">
                          <input type="range" min="0" max="100" value={m.percent} disabled={m.locked} onChange={(e) => setMacros(prev => updateMacrosSafely(type, e.target.value, prev, targetCalories))} className={`w-full h-1.5 rounded-full appearance-none cursor-pointer ${m.locked ? 'bg-moss dark:bg-moss accent-slate-400' : 'bg-moss dark:bg-moss accent-forest'}`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Edit: day structure list */}
              <div>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="flex items-baseline gap-2">
                    <h3 className={`text-sm font-semibold ${themeClasses.textPrimary}`}>{t('dashboard.dayStructure')}</h3>
                    <span className={`text-xs ${themeClasses.textMuted}`}>{editMealsCountLabel}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const unlocked = mealsList.filter(m => !m.locked);
                        if (!unlocked.length) return;
                        const eqCals = Math.round((targetCalories - mealsList.filter(m => m.locked).reduce((s, m) => s + m.calories, 0)) / unlocked.length);
                        setMealsList(mealsList.map(m => m.locked ? m : { ...m, calories: eqCals, macros: rescaleMacrosForMeal(m.macros, eqCals) }));
                      }}
                      className={`text-xs font-medium ${themeClasses.textMuted} hover:text-forest-ondark dark:hover:text-forest-ondark transition-colors`}
                    >
                      {t('dashboard.equalSplit')}
                    </button>
                    <button
                      type="button"
                      onClick={handleAddMeal}
                      disabled={mealsList.length >= MEAL_OPTION_KEYS.length}
                      className={`inline-flex items-center gap-1 text-xs font-semibold text-forest-ondark dark:text-forest-ondark disabled:opacity-40`}
                    >
                      <Plus size={14} /> {t('dashboard.add')}
                    </button>
                  </div>
                </div>

                <ul className={`divide-y ${isDarkMode ? 'divide-line' : 'divide-line'}`}>
                  {mealsList.map((meal, index) => {
                    const usedByOthers = new Set(mealsList.filter((_, i) => i !== index).map((m) => m.nameKey));
                    const availableOptions = MEAL_OPTION_KEYS.filter((k) => k === meal.nameKey || !usedByOthers.has(k));
                    return (
                      <li key={meal.id} className="py-4 flex flex-col lg:flex-row lg:items-center gap-3">
                        <div className="relative lg:w-44 shrink-0">
                          <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setOpenMealSelectIndex((prev) => (prev === index ? null : index)); }}
                            className={`w-full text-left rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2 border ${isDarkMode ? 'bg-transparent border-line text-ink' : 'bg-surface border-line text-ink'}`}
                          >
                            <span className="font-medium truncate">{getMealLabel(meal.nameKey)}</span>
                            <ChevronDown size={14} className="opacity-50 shrink-0" />
                          </button>
                          {openMealSelectIndex === index && (
                            <div onMouseDown={(e) => e.stopPropagation()} className={`absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border max-h-48 overflow-y-auto  ${isDarkMode ? 'bg-canvas border-line' : 'bg-surface border-line'}`}>
                              {availableOptions.map((k) => (
                                <button key={k} type="button" onClick={() => { const n = [...mealsList]; n[index].nameKey = k; setMealsList(n); setOpenMealSelectIndex(null); }} className={`w-full text-left py-2 px-3 text-sm ${isDarkMode ? 'hover:bg-surface text-ink' : 'hover:bg-canvas text-ink'} ${meal.nameKey === k ? 'text-forest-ondark dark:text-forest-ondark font-semibold' : ''}`}>
                                  {getMealLabel(k)}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <input
                          value={meal.description}
                          placeholder={t('dashboard.content')}
                          onChange={(e) => { const n = [...mealsList]; n[index].description = e.target.value; setMealsList(n); }}
                          className={`${themeClasses.inputField} flex-1 min-w-0`}
                        />
                        <div className="flex items-center gap-2 lg:w-auto shrink-0">
                          <input
                            type="number"
                            value={meal.calories}
                            disabled={meal.locked}
                            onChange={(e) => updateMealsSafely(index, e.target.value)}
                            className={`${themeClasses.inputField} w-24 text-center tabular-nums ${meal.locked ? 'opacity-50' : ''}`}
                          />
                          <span className={`text-xs ${themeClasses.textMuted}`}>{t('dashboard.kcal')}</span>
                          <button
                            type="button"
                            onClick={() => { const n = [...mealsList]; n[index].locked = !n[index].locked; setMealsList(n); }}
                            className={`p-2 rounded-lg transition-colors ${meal.locked ? 'text-amber-600 dark:text-amber-400' : `${themeClasses.textMuted} hover:text-ink dark:hover:text-ink`}`}
                            title={meal.locked ? t('dashboard.unlock') : t('dashboard.lock')}
                          >
                            {meal.locked ? <Lock size={15} /> : <Unlock size={15} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteMeal(index)}
                            className={`p-2 rounded-lg ${themeClasses.textMuted} hover:text-red-500 transition-colors`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className={`mt-4 flex justify-end text-sm tabular-nums font-medium ${mealsCalMatched ? themeClasses.textPrimary : 'text-amber-600 dark:text-amber-400'}`}>
                  {t('dashboard.totalCalories')}: {mealsCalSum} / {targetCalories} {t('dashboard.kcal')}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* --- CLIENT SNAPSHOT (plans · chats · weight vs goal + next actions) --- */}
        <ClientSnapshotBar
          stats={stats}
          weightLogs={weightLogs}
          goal={clientData?.goal || selectedClient?.goal}
          language={language}
          onCreatePlan={() => navigate('/create-meal-plan')}
          onOpenChat={() => navigate('/chat')}
        />

        {/* --- BOTTOM DASHBOARD GRIDS (Overview, Consultations, Activity, Weight) --- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Meal Plan Overview */}
          <div className={`${cardBase} p-6 sm:p-8 flex flex-col`}>
            <div className="flex justify-between items-center mb-1">
              <h2 className={`text-xl font-bold ${themeClasses.textPrimary}`}>
                {t('dashboard.mealPlanOverview')}
              </h2>
            </div>
            <p className={`${themeClasses.textMuted} text-sm mb-6`}>
              {t('dashboard.mealPlanOverviewSubtitle')}
            </p>
            {mealPlans.length === 0 ? (
              <div className="text-center py-10 bg-canvas dark:bg-surface/50 rounded-lg border border-dashed border-line dark:border-line my-auto">
                <ChefHat size={40} className="mx-auto mb-4 text-ink-muted dark:text-ink-muted" />
                <p className={`${themeClasses.textSecondary} font-medium text-sm mb-5`}>{t('dashboard.noMealPlans')}</p>
                <button onClick={() => navigate('/create-meal-plan')} className={themeClasses.btnPrimaryPill + ' mx-auto'}>
                  <Plus size={16} /> {t('dashboard.createFirstMealPlan')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {mealPlans.slice(0, 3).map((plan, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-surface border-line' : 'bg-canvas border-line'} flex items-center gap-3`}>
                    <FileText size={18} className={themeClasses.textMuted} />
                    <p className={`${themeClasses.textPrimary} text-sm font-semibold`}>{plan.meal_plan_name || `Plan ${idx + 1}`}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Consultations */}
          <div className={`${cardBase} p-6 sm:p-8 flex flex-col`}>
            <h2 className={`text-xl font-bold ${themeClasses.textPrimary} mb-1`}>
              {t('dashboard.consultations')}
            </h2>
            <p className={`${themeClasses.textMuted} text-sm mb-6`}>
              {t('dashboard.consultationsSubtitle')}
            </p>
            {recentMessages.length === 0 ? (
              <div className="text-center py-10 bg-canvas dark:bg-surface/50 rounded-lg border border-dashed border-line dark:border-line my-auto">
                <MessageCircle size={40} className="mx-auto mb-4 text-ink-muted dark:text-ink-muted" />
                <p className={`${themeClasses.textSecondary} font-medium text-sm`}>{t('dashboard.noRecentActivity')}</p>
              </div>
            ) : (
              <div className="space-y-3 mb-5">
                {recentMessages.map((msg, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-surface border-line' : 'bg-canvas border-line'}`}>
                    <div className="flex items-start justify-between mb-2">
                      <span className={`text-xs font-bold uppercase tracking-wider ${msg.role === 'assistant' ? themeClasses.textGreen : 'text-progress dark:text-progress'}`}>
                        {msg.role === 'assistant' ? 'AI Assistant' : 'Client'}
                      </span>
                      <span className={`text-[10px] font-medium ${themeClasses.textMuted}`}>
                        {formatDate(msg.created_at)}
                      </span>
                    </div>
                    <p className={`text-sm ${themeClasses.textPrimary} line-clamp-2 leading-relaxed`}>
                      {parseMessageContent(msg)}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {conversations.length > 0 && (
              <button onClick={() => navigate('/chat')} className={`${themeClasses.btnSecondaryPill} w-full mt-auto`}>
                {t('dashboard.viewFullChat')}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className={`${cardBase} p-6 sm:p-8 flex flex-col`}>
            <h2 className={`text-xl font-bold ${themeClasses.textPrimary} mb-1`}>
              {t('dashboard.recentActivity')}
            </h2>
            <p className={`${themeClasses.textMuted} text-sm mb-6`}>
              {t('dashboard.recentActivitySubtitle')}
            </p>
            <div className="text-center py-10 bg-canvas dark:bg-surface/50 rounded-lg border border-dashed border-line dark:border-line my-auto">
               <Activity size={40} className="mx-auto mb-4 text-ink-muted dark:text-ink-muted" />
              <p className={`${themeClasses.textSecondary} font-medium text-sm`}>{t('dashboard.noRecentActivity')}</p>
            </div>
          </div>

          {/* Weight Logs */}
          <div className={`${cardBase} p-6 sm:p-8 flex flex-col`}>
            <h2 className={`text-xl font-bold ${themeClasses.textPrimary} mb-1`}>
              {t('dashboard.weightLogs')}
            </h2>
            <p className={`${themeClasses.textMuted} text-sm mb-6`}>
              {t('dashboard.weightLogsSubtitle')}
            </p>
            {weightLogs.length === 0 ? (
              <div className="text-center py-10 bg-canvas dark:bg-surface/50 rounded-lg border border-dashed border-line dark:border-line my-auto">
                <Scale size={40} className="mx-auto mb-4 text-ink-muted dark:text-ink-muted" />
                <p className={`${themeClasses.textSecondary} font-medium text-sm mb-5`}>{t('dashboard.noWeightLogs')}</p>
                <button className={`${themeClasses.btnPrimaryPill} mx-auto`}>
                  <Plus size={16} /> {t('dashboard.addFirstWeightLog')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {weightLogs.slice(-5).reverse().map((log, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-surface border-line' : 'bg-canvas border-line'} flex items-center justify-between`}>
                    <span className={`${themeClasses.textPrimary} text-sm font-bold flex items-center gap-2`}>
                      <Scale size={16} className={themeClasses.textMuted} />
                      {log.weight_kg} {t('dashboard.kg')}
                      {log.body_fat_percentage && <span className={`font-medium ${themeClasses.textMuted}`}>{` • ${log.body_fat_percentage}% BF`}</span>}
                    </span>
                    <span className={`text-xs font-medium ${themeClasses.textMuted}`}>
                      {formatDate(log.measurement_date)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </main>
    </div>
  </ClientWorkspaceLayout>
  );
}

export default DashboardPage;